<!--
  COMPONENTE: OnLineSalesTable.vue
  VERSIÓN: 2.0 - Sistema Unificado
  FECHA: 2024
  
  CAMBIOS IMPORTANTES:
  ==================
  
  1. KPIs CORREGIDOS:
     - Los KPIs ahora se calculan desde props que deben contener TODOS los datos filtrados
     - Ya no se calculan desde 'records' que solo contiene los datos de la página actual
     - Props requeridas: totalFilteredRecords, totalFilteredAmount, totalPaidSales, etc.
  
  2. SISTEMA DE PAGINACIÓN UNIFICADO:
     - Mantiene compatibilidad con scroll infinito (sistema anterior)
     - Agrega navegación por botones de paginación (sistema nuevo)
     - Ambos sistemas funcionan simultáneamente
  
  3. FILTROS SINCRONIZADOS:
     - Los filtros antiguos (selectedStatus, etc.) se sincronizan automáticamente con los nuevos
     - Los filtros avanzados (searchText, platform, etc.) funcionan con debounce
     - Filtros guardados permiten reutilizar configuraciones complejas
  
  4. EVENTOS EMITIDOS:
     - 'searchUnified': Nuevo evento que incluye todos los parámetros unificados
     - 'search': Evento anterior mantenido por compatibilidad
     - 'fetchNewPage': Para scroll infinito (mantenido por compatibilidad)
  
  USO RECOMENDADO:
  ===============
  
  <sales-table
    :records="records"                           // Datos de la página actual
    :loading="loading"                           // Estado de carga
    :totalRecords="totalRecords"                 // Total de registros para paginación
    :totalFilteredRecords="totalFilteredRecords" // Total de registros filtrados (para KPIs)
    :totalFilteredAmount="totalFilteredAmount"   // Monto total filtrado (para KPIs)
    :totalPaidSales="totalPaidSales"             // Ventas pagadas filtradas (para KPIs)
    :totalPendingSales="totalPendingSales"       // Ventas pendientes filtradas (para KPIs)
    :totalErrorSales="totalErrorSales"           // Ventas con error filtradas (para KPIs)
    :totalSuccessfulNotifications="totalSuccessfulNotifications" // Notificaciones exitosas (para KPIs)
    @searchUnified="handleSearchUnified"         // Evento principal (recomendado)
    @search="handleSearch"                       // Evento anterior (compatibilidad)
    @fetchNewPage="handleFetchNewPage"           // Para scroll infinito
  />
  
  ESTADO ACTUAL v2.1 - PAGINACIÓN LOCAL:
  =====================================
  
  ✅ FUNCIONANDO COMPLETAMENTE:
  - Filtros básicos: estado, tipo transacción, sucursal, fechas, ID comercio
  - Paginación LOCAL: navega entre páginas sin consultar endpoint
  - KPIs ESTABLES: calculados una sola vez con todos los datos filtrados
  - Gráficos COMPLETOS: basados en todos los datos filtrados, no solo página actual
  - Sincronización automática de filtros
  - Búsqueda optimizada: obtiene todos los datos de una vez
  
  ⚡ MEJORAS IMPLEMENTADAS:
  - Paginación sin consultas adicionales al endpoint
  - KPIs que no cambian al navegar entre páginas
  - Gráficos que muestran estadísticas completas
  - Mejor rendimiento: una sola consulta masiva vs múltiples consultas por página
  
  🔄 PREPARADO PARA BACKEND:
  - Filtros avanzados: búsqueda por texto, plataforma, rangos de monto, estado notificaciones
  - Ordenamiento dinámico por columnas
  - KPIs optimizados calculados en backend
  
  BACKEND PENDIENTE:
  =================
  
  Para funcionamiento óptimo, el backend debe implementar:
  
  1. Soporte para filtros avanzados en "/v1/sales/get/onlinesales/pag":
     - searchText: búsqueda en texto libre (socio, responsable, email)
     - platform: filtro por plataforma de pago
     - amountFrom/amountTo: rangos de monto
     - notificationStatus: estado de notificaciones
  
  2. Soporte para ordenamiento dinámico:
     - sortField: campo por el cual ordenar
     - sortDirection: 'asc' o 'desc'
  
  3. (OPCIONAL) Endpoint optimizado: "/v1/sales/get/onlinesales/pag/unified"
     Estructura de respuesta recomendada:
     {
       "result": {
         "records": [...],                    // Datos de la página actual
         "pagination": {
           "currentPage": 1,
           "totalPages": 10,
           "totalRecords": 1000,
           "itemsPerPage": 50
         },
         "summary": {                         // KPIs calculados para TODOS los datos filtrados
           "totalRecords": 1000,
           "totalAmount": 500000,
           "paidSales": 800,
           "pendingSales": 150,
           "errorSales": 50,
           "successfulNotifications": 750
         }
       }
     }
  
  COMPATIBILIDAD:
  ==============
  
  - Mantiene 100% de compatibilidad con el sistema anterior
  - Si no se proveen las props de KPIs, se calculan desde 'records' (limitado)
  - Si no se maneja 'searchUnified', funciona con eventos anteriores
  - Scroll infinito y navegación por botones funcionan simultáneamente
-->

<template>
  <div class="online-sales-container">
    <!-- Contenido Principal -->
    <div class="card">
    <div class="card-header bg-success pt-0">
      <!-- Botón de filtros móviles - SIEMPRE ARRIBA -->
      <div class="d-md-none mb-2">
        <button
          class="btn btn-info btn-sm rounded-pill btn-filter--border w-100"
          type="button"
          @click="showMobileFilters = !showMobileFilters"
        >
          <i class="fa fa-sliders me-2" aria-hidden="true"></i>
          {{ showMobileFilters ? 'Ocultar Filtros' : 'Mostrar Filtros' }}
        </button>
      </div>
      
      <!-- Filtros de escritorio - SIEMPRE VISIBLES EN ESCRITORIO -->
      <div class="d-none d-md-block">
        <!-- Primera fila de filtros -->
        <div class="row mb-1">
          <div class="col-md-3 mb-1 filter-field">
            <label class="form-label small text-light mb-1 d-block" for="commerceOrderInput">
              <i class="fas fa-hashtag me-1"></i> ID o Búsqueda
            </label>
            <input
              id="commerceOrderInput"
              type="text"
              class="form-control form-control-sm"
              placeholder="ID o Cliente"
              v-model="commerceOrderFilter"
            />
          </div>
          <div class="col-md-3 mb-1 filter-field">
            <label class="form-label small text-light mb-1 d-block" for="dateFrom">
              <i class="fas fa-calendar-alt me-1"></i> Fecha desde
            </label>
            <input
              id="dateFrom"
              type="date"
              class="form-control form-control-sm"
              v-model="filters.dateFrom"
              :max="filters.dateTo || getTodayDate()"
              @change="validateDateRange"
            />
          </div>
          <div class="col-md-3 mb-1 filter-field">
            <label class="form-label small text-light mb-1 d-block" for="dateTo">
              <i class="fas fa-calendar-check me-1"></i> Fecha hasta
            </label>
            <input
              id="dateTo"
              type="date"
              class="form-control form-control-sm"
              v-model="filters.dateTo"
              :min="filters.dateFrom"
              :max="getTodayDate()"
              @change="validateDateRange"
            />
          </div>
          <div class="col-md-3 mb-1 filter-field">
            <label class="form-label small text-light mb-1 d-block" for="statusSelectOnline">
              <i class="fas fa-check-circle me-1"></i> Estado
            </label>
            <el-select
              id="statusSelectOnline"
              size="small"
              placeholder="Todos los estados"
              class="w-100"
              v-model="selectedStatus"
            >
              <el-option value="">Todos los estados</el-option>
              <el-option
                v-for="(cat, i) in statusCatalog"
                :key="i"
                :value="cat"
                :label="cat"
              ></el-option>
            </el-select>
          </div>
        </div>
        
        <!-- Segunda fila de filtros y botones -->
        <div class="row mb-1">
          <div class="col-md-3 mb-1 filter-field">
            <label class="form-label small text-light mb-1 d-block" for="branchSelectOnline">
              <i class="fas fa-building me-1"></i> Sucursal
            </label>
            <el-select
              id="branchSelectOnline"
              size="small"
              placeholder="Todas las sucursales"
              class="w-100"
              v-model="selectedBranch"
            >
              <el-option value="">Todas las sucursales</el-option>
              <el-option
                v-for="(branch, i) in branches"
                :key="i"
                :value="branch._id"
                :label="branch.nombreSucursal"
              ></el-option>
            </el-select>
          </div>
          <div class="col-md-3 mb-1 filter-field">
            <label class="form-label small text-light mb-1 d-block" for="trxTypeSelectOnline">
              <i class="fas fa-tags me-1"></i> Tipo Transacción
            </label>
            <el-select
              id="trxTypeSelectOnline"
              size="small"
              placeholder="Todos los tipos"
              class="w-100"
              v-model="selectedTrxType"
            >
              <el-option value="">Todos los tipos</el-option>
              <el-option
                v-for="(cat, i) in trxTypeCatalog"
                :key="i"
                :value="cat"
                :label="cat"
              ></el-option>
            </el-select>
          </div>
          <div class="col-md-6 mb-1">
            <div class="d-flex justify-content-end align-items-end h-100">
              <button
                class="btn btn-info btn-sm rounded-pill btn-filter--border text-nowrap me-2"
                type="button"
                @click="search"
                :disabled="loading || isSearching"
              >
                <i v-if="isSearching" class="fas fa-spinner fa-spin me-1"></i>
                <i v-else class="fa fa-search me-1" aria-hidden="true"></i>
                {{ isSearching ? 'Buscando...' : 'Buscar' }}
              </button>
              
              <button
                class="btn btn-success btn-sm btn-filter--border"
                type="button"
                @click="exportRecords"
                :disabled="loading || isExporting || (!records || records.length === 0)"
                title="Exportar a Excel"
              >
                <i v-if="isExporting" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                <i v-else class="fa fa-file-excel" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
      
        <!-- Segunda fila: Botones de paneles centrados -->
        <div class="d-none d-md-flex justify-content-center pt-2 border-top border-light border-opacity-25">
          <div class="btn-group" role="group">
            <button
              class="btn btn-sm rounded-pill btn-filter--border text-nowrap panel-toggle-btn"
              :class="{ 'active': showAdvancedFilters }"
              type="button"
              @click="showAdvancedFilters = !showAdvancedFilters"
              title="Mostrar/Ocultar filtros avanzados"
            >
              <i class="fas fa-filter me-2" aria-hidden="true"></i>
              Avanzados
            </button>
            
            <button
              class="btn btn-sm rounded-pill btn-filter--border mx-2 text-nowrap panel-toggle-btn"
              :class="{ 'active': showChartsPanel }"
              type="button"
              @click="showChartsPanel = !showChartsPanel"
              title="Mostrar/Ocultar gráficos"
            >
              <i class="fas fa-chart-bar me-2" aria-hidden="true"></i>
              Gráficos
            </button>
            
            <button
              class="btn btn-sm rounded-pill btn-filter--border text-nowrap panel-toggle-btn"
              :class="{ 'active': showSavedFilters }"
              type="button"
              @click="showSavedFilters = !showSavedFilters"
              title="Mostrar/Ocultar filtros guardados"
            >
              <i class="fas fa-bookmark me-2" aria-hidden="true"></i>
              Guardados
            </button>
          </div>
        </div>
      </div>
      
      
      <!-- Panel de filtros móviles -->
      <div v-if="showMobileFilters" class="d-md-none mobile-filters mx-3 mt-2">
          <div class="mb-3">
            <label class="form-label small text-white fw-bold mb-2 d-block" for="searchTextMobile">
              <i class="fas fa-search me-2"></i>Búsqueda general
            </label>
            <input
              id="searchTextMobile"
              type="text"
              class="form-control form-control-sm"
              placeholder="ID o Cliente"
              v-model="commerceOrderFilter"
              @keyup.enter="search"
            />
          </div>
          
          <div class="row mb-3">
            <div class="col-6 pe-2">
              <label class="form-label small text-white fw-bold mb-2 d-block" for="dateFromMobile">
                <i class="fas fa-calendar-alt me-2"></i>Fecha desde
              </label>
              <input
                id="dateFromMobile"
                type="date"
                class="form-control form-control-sm"
                v-model="dateFilterFrom"
                :max="dateFilterTo || getTodayDate()"
              />
            </div>
            <div class="col-6 ps-2">
              <label class="form-label small text-white fw-bold mb-2 d-block" for="dateToMobile">
                <i class="fas fa-calendar-check me-2"></i>Fecha hasta
              </label>
              <input
                id="dateToMobile"
                type="date"
                class="form-control form-control-sm"
                v-model="dateFilterTo"
                :min="dateFilterFrom"
                :max="getTodayDate()"
              />
            </div>
          </div>
          
          <div class="row mb-3">
            <div class="col-6 pe-2">
              <label class="form-label small text-white fw-bold mb-2 d-block" for="statusMobile">
                <i class="fas fa-clipboard-check me-2"></i>Estado
              </label>
              <el-select 
                id="statusMobile" 
                size="small" 
                v-model="selectedStatus"
                class="w-100"
                placeholder="Todos los estados"
              >
                <el-option value="">Todos los estados</el-option>
                <el-option
                  v-for="(cat, i) in statusCatalog"
                  :key="i"
                  :value="cat"
                  :label="cat"
                ></el-option>
              </el-select>
            </div>
            <div class="col-6 ps-2">
              <label class="form-label small text-white fw-bold mb-2 d-block" for="trxTypeMobile">
                <i class="fas fa-tags me-2"></i>Tipo
              </label>
              <el-select 
                id="trxTypeMobile" 
                size="small" 
                v-model="selectedTrxType"
                class="w-100"
                placeholder="Todos los tipos"
              >
                <el-option value="">Todos los tipos</el-option>
                <el-option
                  v-for="(cat, i) in trxTypeCatalog"
                  :key="i"
                  :value="cat"
                  :label="cat"
                ></el-option>
              </el-select>
            </div>
          </div>
          
          <div class="mb-3">
            <label class="form-label small text-white fw-bold mb-2 d-block" for="branchMobile">
              <i class="fas fa-building me-2"></i>Sucursal
            </label>
            <el-select 
              id="branchMobile" 
              size="small" 
              v-model="selectedBranch"
              class="w-100"
              placeholder="Todas las sucursales"
            >
              <el-option value="">Todas las sucursales</el-option>
              <el-option
                v-for="(branch, i) in branches"
                :key="i"
                :label="branch.nombreSucursal"
                :value="branch._id"
              ></el-option>
            </el-select>
          </div>
          
          <!-- Botones de panel en mobile -->
          <div class="btn-group btn-group-sm w-100 mb-2" role="group">
            <button 
              type="button" 
              class="btn btn-sm btn-filter--border panel-toggle-btn mobile-btn-consistent"
              :class="{ 'active': showAdvancedFilters }"
              @click="showAdvancedFilters = !showAdvancedFilters"
            >
              <i class="fas fa-filter me-1" aria-hidden="true"></i>
              Avanzados
            </button>
            <button 
              type="button" 
              class="btn btn-sm btn-filter--border panel-toggle-btn mobile-btn-consistent"
              :class="{ 'active': showChartsPanel }"
              @click="showChartsPanel = !showChartsPanel"
            >
              <i class="fas fa-chart-bar me-1" aria-hidden="true"></i>
              Gráficos
            </button>
            <button 
              type="button" 
              class="btn btn-sm btn-filter--border panel-toggle-btn mobile-btn-consistent"
              :class="{ 'active': showSavedFilters }"
              @click="showSavedFilters = !showSavedFilters"
            >
              <i class="fas fa-bookmark me-1" aria-hidden="true"></i>
              Guardados
            </button>
          </div>
          
          <!-- Botones de búsqueda y exportación móvil -->
          <div class="btn-group btn-group-sm w-100 mt-2" role="group">
            <button 
              type="button" 
              class="btn btn-sm btn-info btn-filter--border mobile-btn-consistent"
              @click="search"
              :disabled="loading || isSearching"
            >
              <i v-if="isSearching" class="fas fa-spinner fa-spin me-1"></i>
              <i v-else class="fa fa-search me-1" aria-hidden="true"></i>
              {{ isSearching ? 'Buscando...' : 'Buscar' }}
            </button>
            <button 
              type="button" 
              class="btn btn-sm btn-success btn-filter--border mobile-btn-consistent"
              @click="exportRecords"
              :disabled="loading || isExporting || (!records || records.length === 0)"
              title="Exportar a Excel"
            >
              <i v-if="isExporting" class="fas fa-spinner fa-spin me-1"></i>
              <i v-else class="fa fa-file-excel me-1" aria-hidden="true"></i>
              Excel
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Panel de Filtros Avanzados -->
    <div v-if="showAdvancedFilters" class="card mb-3 border-primary">
      <div class="card-header bg-primary text-white" style="padding: 0.375rem 1rem;">
        <div class="d-flex justify-content-between align-items-center">
          <h6 class="mb-0">
            <i class="fas fa-filter me-2"></i>
            Filtros Avanzados - Ventas Online
          </h6>
          <button 
            type="button" 
            class="btn btn-sm btn-light"
            @click="showAdvancedFilters = false"
            title="Cerrar panel"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
      <div class="card-body">
        <!-- Mensaje informativo sobre filtros avanzados -->
        <div class="alert alert-info alert-sm mb-3" role="alert">
        <div class="d-flex align-items-center">
          <i class="fas fa-info-circle me-2"></i>
          <small>
            <strong>Filtros Inteligentes:</strong> Los filtros avanzados se aplican automáticamente (1 segundo de delay) 
            sobre los datos en memoria. Los botones de fecha también filtran localmente. 
            Los KPIs y gráficos se actualizan dinámicamente.
          </small>
        </div>
      </div>
      
      <!-- Indicador de filtrado en progreso -->
      <div v-if="isApplyingAdvancedFilters" class="alert alert-warning alert-sm mb-3" role="alert">
        <div class="d-flex align-items-center">
          <div class="spinner-border spinner-border-sm me-2" role="status">
            <span class="visually-hidden"></span>
          </div>
          <small>
            <strong>Aplicando filtros...</strong> Los datos se actualizarán en un momento.
          </small>
        </div>
      </div>
      
      <div class="row align-items-end">
        <div class="col-md-3 mb-2">
          <label class="form-label small text-muted mb-1">
            Buscar por texto
            <span class="badge badge-success ms-1" title="Filtro local activo">✓</span>
          </label>
          <input 
            type="text" 
            class="form-control form-control-sm" 
            placeholder="Socio, responsable, email..."
            v-model="filters.searchText"
            @input="handleAdvancedFilterChange"
            title="Filtra sobre los datos ya cargados"
          >
        </div>
        <div class="col-md-2 mb-2">
          <label class="form-label small text-muted mb-1">
            Plataforma
            <span class="badge badge-success ms-1" title="Filtro local activo">✓</span>
          </label>
          <select 
            class="form-select form-select-sm" 
            v-model="filters.platform" 
            @change="handleAdvancedFilterChange"
            title="Filtra sobre los datos ya cargados"
          >
            <option value="">Todas</option>
            <option value="FLOW">FLOW</option>
            <option value="WEBPAY">WEBPAY</option>
            <option value="PAYPAL">PAYPAL</option>
          </select>
        </div>
        <div class="col-md-2 mb-2">
          <label class="form-label small text-muted mb-1">
            Monto desde
            <span class="badge badge-success ms-1" title="Filtro local activo">✓</span>
          </label>
          <input 
            type="number" 
            class="form-control form-control-sm" 
            placeholder="0"
            v-model="filters.amountFrom"
            @input="handleAdvancedFilterChange"
            title="Filtra sobre los datos ya cargados"
          >
        </div>
        <div class="col-md-2 mb-2">
          <label class="form-label small text-muted mb-1">
            Monto hasta
            <span class="badge badge-success ms-1" title="Filtro local activo">✓</span>
          </label>
          <input 
            type="number" 
            class="form-control form-control-sm" 
            placeholder="999999"
            v-model="filters.amountTo"
            @input="handleAdvancedFilterChange"
            title="Filtra sobre los datos ya cargados"
          >
        </div>
        <div class="col-md-2 mb-2">
          <label class="form-label small text-muted mb-1">
            Estado Notif.
            <span class="badge badge-success ms-1" title="Filtro local activo">✓</span>
          </label>
          <select 
            class="form-select form-select-sm" 
            v-model="filters.notificationStatus" 
            @change="handleAdvancedFilterChange"
            title="Filtra sobre los datos ya cargados"
          >
            <option value="">Todos</option>
            <option value="success">Exitosas</option>
            <option value="error">Con Errores</option>
            <option value="pending">Pendientes</option>
          </select>
        </div>
        <div class="col-md-1 mb-2">
          <div class="d-flex flex-column">
            <button 
              type="button" 
              class="btn btn-outline-secondary btn-sm w-100 mb-1"
              @click="clearAllFilters"
              title="Limpiar todos los filtros"
            >
              <i class="fas fa-times"></i>
            </button>
            <button 
              type="button" 
              class="btn btn-outline-warning btn-sm w-100"
              @click="clearAdvancedFilters"
              title="Limpiar solo filtros avanzados"
              v-if="hasAdvancedFiltersApplied"
            >
              <i class="fas fa-undo"></i>
            </button>
          </div>
        </div>
      </div>
      
      <!-- Filtros de fecha rápidos -->
      <div class="row mt-2">
        <div class="col-12">
          <div class="d-flex justify-content-between align-items-center">
            <div class="btn-group btn-group-sm" role="group">
              <button 
                type="button" 
                class="btn btn-outline-primary"
                @click="setDateFilter('today')"
              >
                Hoy
              </button>
              <button 
                type="button" 
                class="btn btn-outline-primary"
                @click="setDateFilter('yesterday')"
              >
                Ayer
              </button>
              <button 
                type="button" 
                class="btn btn-outline-primary"
                @click="setDateFilter('week')"
              >
                Esta Semana
              </button>
              <button 
                type="button" 
                class="btn btn-outline-primary"
                @click="setDateFilter('month')"
              >
                Este Mes
              </button>
              <button 
                type="button" 
                class="btn btn-outline-secondary"
                @click="clearDateFilters"
              >
                Limpiar Fechas
              </button>
            </div>
            <button 
              type="button" 
              class="btn btn-danger btn-sm"
              @click="exportKPIsToPDF"
              title="Exportar KPIs a PDF"
            >
              <i class="fas fa-file-pdf me-1"></i>
              EXPORTAR KPIS PDF
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
    
    <div class="card-body">
      <!-- Resumen de Totales (SIEMPRE VISIBLE - incluso sin datos) -->
      <div class="card mb-3 bg-light" ref="kpisSection">
        <div class="card-body">
          <div class="row text-center">
            <div class="col-lg-3 col-md-6 mb-3 mb-lg-0">
              <div class="d-flex align-items-center justify-content-center">
                <div class="icon-shape bg-primary text-white rounded-circle me-3">
                  <i class="fas fa-shopping-cart"></i>
                </div>
                <div>
                  <h5 class="mb-0 text-primary">{{ totalOnlineSales }}</h5>
                  <small class="text-muted">Total Ventas Online</small>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-6 mb-3 mb-lg-0">
              <div class="d-flex align-items-center justify-content-center">
                <div class="icon-shape bg-success text-white rounded-circle me-3">
                  <i class="fas fa-dollar-sign"></i>
                </div>
                <div>
                  <h5 class="mb-0 text-success">{{ formatCurrency(totalAmount) }}</h5>
                  <small class="text-muted">Monto Total</small>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-6 mb-3 mb-md-0">
              <div class="d-flex align-items-center justify-content-center">
                <div class="icon-shape bg-info text-white rounded-circle me-3">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div>
                  <h5 class="mb-0 text-info">{{ paidSales }}</h5>
                  <small class="text-muted">Pagos Exitosos</small>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-6">
              <div class="d-flex align-items-center justify-content-center">
                <div class="icon-shape bg-warning text-white rounded-circle me-3">
                  <i class="fas fa-clock"></i>
                </div>
                <div>
                  <h5 class="mb-0 text-warning">{{ pendingSales }}</h5>
                  <small class="text-muted">Pagos Pendientes</small>
                </div>
              </div>
            </div>
          </div>
          <!-- Información adicional -->
          <div class="row mt-3 pt-3 border-top">
            <div class="col-md-3 text-center">
              <small class="text-muted">
                <i class="fas fa-chart-line me-1"></i>
                Promedio por venta: <strong>{{ formatCurrency(averageSaleAmount) }}</strong>
              </small>
            </div>
            <div class="col-md-3 text-center">
              <small class="text-muted">
                <i class="fas fa-percentage me-1"></i>
                Tasa de éxito: <strong>{{ successPercentage }}%</strong>
              </small>
            </div>
            <div class="col-md-3 text-center">
              <small class="text-muted">
                <i class="fas fa-envelope me-1"></i>
                Notificaciones exitosas: <strong>{{ notificationSuccessPercentage }}%</strong>
              </small>
            </div>
            <div class="col-md-3 text-center">
              <small class="text-muted">
                <i class="fas fa-calendar-day me-1"></i>
                Última actualización: <strong>{{ getCurrentDateTime() }}</strong>
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Panel de Gráficos -->
      <div v-if="showChartsPanel" class="card mb-3 border-warning" ref="chartsSection">
        <div class="card-header bg-warning text-dark" style="padding: 0.375rem 1rem;">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="mb-0">
              <i class="fas fa-chart-bar me-2"></i>
              {{ tituloAnalisisVisual }}
            </h6>
            <div class="d-flex gap-2">
              <button 
                type="button" 
                class="btn btn-sm btn-danger"
                @click="exportKPIsToPDF"
                title="Exportar KPIs a PDF"
              >
                <i class="fas fa-file-pdf me-1"></i>
                Exportar PDF
              </button>
              <button 
                type="button" 
                class="btn btn-sm btn-dark"
                @click="showChartsPanel = false"
                title="Cerrar panel"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-lg-6 mb-4">
              <h6 class="text-muted mb-3">
                <i class="fas fa-chart-pie me-2 text-success"></i>
                Ventas por Estado
              </h6>
              <div 
                class="chart-container" 
                style="height: 200px; position: relative;"
                :class="{ 'no-data': chartData.salesByStatus.length === 0 }"
              >
                <!-- Indicador de carga del gráfico -->
                <div v-if="chartLoadingStates.status" class="chart-loading-overlay">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Cargando gráfico...</span>
                  </div>
                  <p class="mt-2 text-muted small">Procesando datos...</p>
                </div>
                <canvas ref="statusChart" v-show="chartData.salesByStatus.length > 0 && !chartLoadingStates.status"></canvas>
              </div>
            </div>
            <div class="col-lg-6 mb-4">
              <h6 class="text-muted mb-3">
                <i class="fas fa-chart-bar me-2 text-primary"></i>
                Ventas por Tipo de Transacción
              </h6>
              <div 
                class="chart-container" 
                style="height: 200px; position: relative;"
                :class="{ 'no-data': chartData.salesByType.length === 0 }"
              >
                <!-- Indicador de carga del gráfico -->
                <div v-if="chartLoadingStates.type" class="chart-loading-overlay">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Cargando gráfico...</span>
                  </div>
                  <p class="mt-2 text-muted small">Procesando datos...</p>
                </div>
                <canvas ref="typeChart" v-show="chartData.salesByType.length > 0 && !chartLoadingStates.type"></canvas>
              </div>
            </div>
            <div class="col-lg-6 mb-4">
              <h6 class="text-muted mb-3">
                <i class="fas fa-building me-2 text-info"></i>
                Ventas por Sucursal
              </h6>
              <div 
                class="chart-container" 
                style="height: 200px; position: relative;"
                :class="{ 'no-data': chartData.salesByBranch.length === 0 }"
              >
                <!-- Indicador de carga del gráfico -->
                <div v-if="chartLoadingStates.branch" class="chart-loading-overlay">
                  <div class="spinner-border text-info" role="status">
                    <span class="visually-hidden">Cargando gráfico...</span>
                  </div>
                  <p class="mt-2 text-muted small">Procesando datos...</p>
                </div>
                <canvas ref="branchChart" v-show="chartData.salesByBranch.length > 0 && !chartLoadingStates.branch"></canvas>
              </div>
            </div>
            <div class="col-lg-6 mb-4">
              <h6 class="text-muted mb-3">
                <i class="fas fa-credit-card me-2 text-secondary"></i>
                Ventas por Plataforma
              </h6>
              <div 
                class="chart-container" 
                style="height: 200px;"
                :class="{ 'no-data': chartData.salesByPlatform.length === 0 }"
              >
                <canvas ref="platformChart" v-show="chartData.salesByPlatform.length > 0"></canvas>
              </div>
            </div>
            <div class="col-lg-6 mb-4">
              <h6 class="text-muted mb-3">
                <i class="fas fa-chart-line me-2 text-warning"></i>
                Tendencia Diaria
              </h6>
              <div 
                class="chart-container" 
                style="height: 200px;"
                :class="{ 'no-data': chartData.dailyTrend.length === 0 }"
              >
                <canvas ref="trendChart" v-show="chartData.dailyTrend.length > 0"></canvas>
              </div>
            </div>
            <div class="col-lg-6 mb-4">
              <h6 class="text-muted mb-3">
                <i class="fas fa-envelope me-2 text-danger"></i>
                Estado de Notificaciones
              </h6>
              <div 
                class="chart-container" 
                style="height: 200px;"
                :class="{ 'no-data': chartData.notificationStatus.length === 0 }"
              >
                <canvas ref="notificationChart" v-show="chartData.notificationStatus.length > 0"></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Panel de Filtros Guardados -->
      <div v-if="showSavedFilters" class="card mb-3 border-primary">
        <div class="card-header bg-primary text-white" style="padding: 0.375rem 1rem;">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="mb-0">
              <i class="fas fa-bookmark me-2"></i>
              Filtros Guardados - Ventas Online
            </h6>
            <div class="d-flex">
              <button 
                type="button" 
                class="btn btn-sm btn-light me-2"
                @click="openSaveFilterModal"
                title="Guardar filtro actual"
              >
                <i class="fas fa-save me-1"></i>
                Guardar Actual
              </button>
              <button 
                type="button" 
                class="btn btn-sm btn-light"
                @click="showSavedFilters = false"
                title="Cerrar panel"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="card-body">
          <div v-if="sortedSavedFilters.length === 0" class="text-center py-5">
            <div class="empty-state">
              <i class="fas fa-bookmark fa-3x mb-3 text-muted opacity-50"></i>
              <h5 class="text-muted mb-2">📁 No hay filtros guardados</h5>
              <p class="text-muted mb-3">
                ¡Optimiza tu trabajo guardando configuraciones de filtros frecuentes!
              </p>
              <div class="alert alert-info mx-auto" style="max-width: 500px;">
                <h6 class="alert-heading">💡 ¿Cómo usar los filtros guardados?</h6>
                <ol class="mb-0 text-left small">
                  <li>Configura tus filtros favoritos (fechas, estado, tipo, etc.)</li>
                  <li>Haz clic en <span class="badge bg-primary">Guardar Actual</span></li>
                  <li>Dale un nombre descriptivo a tu filtro</li>
                  <li>Úsalo cuando lo necesites con un solo clic</li>
                </ol>
              </div>
              <button 
                type="button" 
                class="btn btn-primary btn-sm"
                @click="openSaveFilterModal"
              >
                <i class="fas fa-plus me-2"></i>
                Crear Mi Primer Filtro
              </button>
            </div>
          </div>
          <div v-else class="row">
            <div 
              v-for="filter in sortedSavedFilters" 
              :key="filter.id" 
              class="col-lg-4 col-md-6 col-xl-3 mb-3"
            >
              <div class="card border-light h-100 saved-filter-card">
                <div class="card-header bg-light" style="padding: 0.375rem 0.75rem;">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="card-title mb-0 text-truncate fw-bold" :title="filter.name">
                      <i class="fas fa-bookmark me-2 text-primary"></i>
                      {{ filter.name }}
                    </h6>
                    <span class="badge bg-secondary">{{ getFilterSummary(filter) }}</span>
                  </div>
                </div>
                <div class="card-body p-3">
                  <p class="card-text text-muted small mb-2" :title="filter.description">
                    {{ filter.description || 'Sin descripción' }}
                  </p>
                  
                  <!-- Resumen de filtros aplicados -->
                  <div class="filter-tags mb-3">
                    <span v-if="filter.filters.status" class="badge bg-info me-1 mb-1">
                      Estado: {{ filter.filters.status }}
                    </span>
                    <span v-if="filter.filters.trxType" class="badge bg-success me-1 mb-1">
                      Tipo: {{ filter.filters.trxType }}
                    </span>
                    <span v-if="filter.filters.platform" class="badge bg-warning text-dark me-1 mb-1">
                      {{ filter.filters.platform }}
                    </span>
                    <span v-if="filter.filters.dateFrom && filter.filters.dateTo" class="badge bg-secondary me-1 mb-1">
                      📅 {{ filter.filters.dateFrom }} - {{ filter.filters.dateTo }}
                    </span>
                    <span v-if="filter.filters.searchText" class="badge bg-primary me-1 mb-1">
                      🔍 "{{ filter.filters.searchText }}"
                    </span>
                    <span v-if="filter.filters.amountFrom || filter.filters.amountTo" class="badge bg-dark text-white me-1 mb-1">
                      💰 {{ formatAmountRange(filter.filters.amountFrom, filter.filters.amountTo) }}
                    </span>
                  </div>
                  
                  <!-- Información temporal -->
                  <div class="text-muted small mb-3">
                    <i class="fas fa-calendar me-1"></i>
                    Creado: {{ formatDate(filter.createdAt) }}
                    <br>
                    <i class="fas fa-sort me-1"></i>
                    Orden: {{ filter.sorting.field }} ({{ filter.sorting.direction === 'asc' ? '↑' : '↓' }})
                  </div>
                  
                  <!-- Botones de acción -->
                  <div class="d-grid gap-2">
                    <!-- Botón principal: Aplicar/Activar filtro -->
                    <button 
                      type="button" 
                      class="btn btn-primary btn-sm"
                      @click="applySavedFilter(filter)"
                      title="Aplicar este filtro"
                    >
                      <i class="fas fa-play me-2"></i>
                      Aplicar Filtro
                    </button>
                    
                    <!-- Botones secundarios -->
                    <div class="btn-group" role="group">
                      <button 
                        type="button" 
                        class="btn btn-outline-info btn-sm"
                        @click="editSavedFilter(filter)"
                        title="Editar filtro"
                      >
                        <i class="fas fa-edit me-1"></i>
                        Editar
                      </button>
                      <button 
                        type="button" 
                        class="btn btn-outline-success btn-sm"
                        @click="duplicateSavedFilter(filter)"
                        title="Duplicar filtro"
                      >
                        <i class="fas fa-copy me-1"></i>
                        Copiar
                      </button>
                      <button 
                        type="button" 
                        class="btn btn-outline-danger btn-sm"
                        @click="deleteSavedFilter(filter)"
                        title="Eliminar filtro"
                      >
                        <i class="fas fa-trash me-1"></i>
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabla Mejorada -->
      <div class="table-responsive position-relative">
        <!-- Overlay de carga -->
        <div 
          v-if="loading" 
          class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
          style="z-index: 10;"
        >
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden"></span>
          </div>
        </div>

        <table class="table table-hover" :class="{ 'opacity-50': loading }">
          <thead>
            <tr>
              <th width="50">
                <div class="form-check">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    :checked="allSelected"
                    @change="toggleSelectAll"
                    :disabled="loading || !records || records.length === 0"
                    title="Seleccionar/Deseleccionar todo"
                  >
                </div>
              </th>
              <th 
                class="sortable-header" 
                @click="sortBy('commerceOrder')"
                :class="getSortClass('commerceOrder')"
                title="Ordenar por ID"
              >
                ID
                <i class="fas ms-1" :class="getSortIcon('commerceOrder')"></i>
              </th>
              <th 
                class="sortable-header" 
                @click="sortBy('itemVendido.valorPlan')"
                :class="getSortClass('itemVendido.valorPlan')"
                title="Ordenar por Monto"
              >
                Monto
                <i class="fas ms-1" :class="getSortIcon('itemVendido.valorPlan')"></i>
              </th>
              <th 
                class="sortable-header" 
                @click="sortBy('fechaCreacion')"
                :class="getSortClass('fechaCreacion')"
                title="Ordenar por Fecha"
              >
                Fecha
                <i class="fas ms-1" :class="getSortIcon('fechaCreacion')"></i>
              </th>
              <th 
                class="sortable-header" 
                @click="sortBy('tipoVenta')"
                :class="getSortClass('tipoVenta')"
                title="Ordenar por Tipo"
              >
                Tipo Venta
                <i class="fas ms-1" :class="getSortIcon('tipoVenta')"></i>
              </th>
              <th 
                class="sortable-header" 
                @click="sortBy('responsable')"
                :class="getSortClass('responsable')"
                title="Ordenar por Responsable"
              >
                Responsable
                <i class="fas ms-1" :class="getSortIcon('responsable')"></i>
              </th>
              <th 
                class="sortable-header" 
                @click="sortBy('estado')"
                :class="getSortClass('estado')"
                title="Ordenar por Estado"
              >
                Estado
                <i class="fas ms-1" :class="getSortIcon('estado')"></i>
              </th>
              <th 
                class="sortable-header" 
                @click="sortBy('plataforma')"
                :class="getSortClass('plataforma')"
                title="Ordenar por Plataforma"
              >
                Plataforma
                <i class="fas ms-1" :class="getSortIcon('plataforma')"></i>
              </th>
              <th>Socio</th>
              <th 
                class="sortable-header" 
                @click="sortBy('nombreSucursal')"
                :class="getSortClass('nombreSucursal')"
                title="Ordenar por Sucursal"
              >
                Sucursal
                <i class="fas ms-1" :class="getSortIcon('nombreSucursal')"></i>
              </th>
              <th>Notificaciones</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <!-- Mensaje cuando no hay registros -->
            <tr v-if="!loading && (!records || records.length === 0)">
              <td colspan="12" class="text-center py-4 text-muted">
                <i class="fas fa-search fa-2x mb-2"></i>
                <p class="mb-0">No se encontraron ventas online con los filtros aplicados</p>
              </td>
            </tr>
            
            <!-- Registros de ventas online -->
            <tr 
              v-for="record in records" 
              :key="record._id"
              :class="{ 'table-primary': selectedItems.includes(record._id) }"
            >
              <td>
                <div class="form-check">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    :value="record._id"
                    v-model="selectedItems"
                    :disabled="loading"
                  >
                </div>
              </td>
              <td>
                <small class="text-monospace fw-bold">{{ record.commerceOrder }}</small>
              </td>
              <td>
                <strong class="text-success">{{ formatCurrency(record.itemVendido.valorPlan) }}</strong>
              </td>
              <td>
                <div class="d-flex flex-column">
                  <small class="fw-bold">{{ formatDate(record.fechaCreacion) }}</small>
                  <small class="text-muted">{{ formatTime(record.fechaCreacion) }}</small>
                </div>
              </td>
              <td>
                <span class="badge bg-info text-dark">{{ record.tipoVenta }}</span>
              </td>
              <td>
                <small class="text-truncate d-block" style="max-width: 120px;">
                  {{ record.responsable }}
                </small>
              </td>
              <td>
                <span :class="getStatusBadgeClass(record.estado)">
                  {{ record.estado }}
                </span>
              </td>
              <td>
                <span class="badge bg-secondary">{{ record.plataforma }}</span>
              </td>
              <td>
                <div class="d-flex flex-column">
                  <small class="fw-bold text-truncate" style="max-width: 120px;" 
                         :title="record.socio.nombre">
                    {{ record.socio.nombre }}
                  </small>
                  <small class="text-muted">{{ record.socio.rut }}</small>
                  <small class="text-muted text-truncate" style="max-width: 120px;" 
                         :title="record.socio.email">
                    {{ record.socio.email }}
                  </small>
                </div>
              </td>
              <td>
                <small class="text-truncate d-block" style="max-width: 100px;" 
                       :title="record.nombreSucursal">
                  {{ record.nombreSucursal }}
                </small>
              </td>
              <td>
                <div class="d-flex justify-content-center">
                  <!-- Notificación de creación -->
                  <div class="me-3" :title="getNotificationCreationTooltip(record)">
                    <i class="fas fa-envelope" 
                       :class="getNotificationCreationClass(record)"></i>
                  </div>
                  
                  <!-- Separación entre íconos -->
                  <div class="mx-1"></div>
                  
                  <!-- Notificación de pago -->
                  <div v-if="record.socio.notificacionPagoExitoso" 
                       :title="getNotificationPaymentTooltip(record)"
                       class="ms-2">
                    <i class="fas fa-envelope-open-text" 
                       :class="getNotificationPaymentClass(record)"></i>
                  </div>
                </div>
              </td>
              <td>
                <div class="btn-group" role="group">
                  <!-- Copiar Link de Pago -->
                  <button 
                    class="btn btn-sm btn-info me-1"
                    @click="copyLink(record.linkPago)"
                    :disabled="!record.linkPago || loading"
                    :title="record.linkPago ? 'Copiar Link de pago' : 'Link no generado'"
                  >
                    <i class="fas fa-copy"></i>
                  </button>
                  <!-- Ver Detalles -->
                  <button 
                    class="btn btn-sm btn-primary me-1"
                    @click="showDetails(record)"
                    title="Ver detalles completos"
                    :disabled="loading"
                  >
                    <i class="fas fa-search-plus"></i>
                  </button>
                  <!-- Reenviar Notificación -->
                  <button 
                    class="btn btn-sm btn-warning me-1"
                    @click="resendNotification(record)"
                    title="Reenviar notificación"
                    :disabled="loading || isResendingNotification"
                    v-if="canResendNotification(record)"
                  >
                    <i v-if="isResendingNotification" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-envelope"></i>
                  </button>
                  <!-- Cancelar/Anular -->
                  <button 
                    class="btn btn-sm btn-danger"
                    @click="confirmCancelSale(record)"
                    title="Cancelar venta"
                    :disabled="loading || isCancelling"
                    v-if="canCancelSale(record)"
                  >
                    <i v-if="isCancelling" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-times-circle"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="records && records.length > 0" class="row mt-3">
        <div class="col-12">
          <!-- Contenedor de paginación visible -->
          <div class="pagination-container bg-light border rounded p-3 mb-3">
            <h6 class="text-muted mb-3">
              <i class="fas fa-list-ol me-2"></i>
              Controles de Paginación
            </h6>
            
            <!-- Información de registros y elementos por página -->
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div class="d-flex align-items-center">
                <small class="text-muted me-3">{{ recordsInfo }}</small>
                <div class="d-flex align-items-center">
                  <label class="form-label small text-muted mb-0 me-2">Mostrar:</label>
                  <select 
                    class="form-select form-select-sm" 
                    style="width: auto;"
                    v-model="pagination.itemsPerPage" 
                    @change="changeItemsPerPage($event.target.value)"
                    :disabled="loading"
                  >
                    <option :value="20">20</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                    <option :value="200">200</option>
                  </select>
                  <small class="text-muted ms-2">por página</small>
                </div>
              </div>
              
              <!-- Información adicional -->
              <div class="text-end">
                <small class="text-muted">
                  Página {{ pagination.currentPage }} de {{ totalPages || 1 }}
                </small>
              </div>
            </div>
            
            <!-- Controles de navegación (solo si hay más de una página) -->
            <nav aria-label="Paginación de ventas online" v-if="totalPages > 1">
              <ul class="pagination justify-content-center mb-0">
                <!-- Primera página -->
                <li class="page-item" :class="{ disabled: pagination.currentPage === 1 || loading }">
                  <button 
                    class="page-link" 
                    @click="goToFirstPage"
                    :disabled="pagination.currentPage === 1 || loading"
                    title="Primera página"
                  >
                    <i class="fas fa-angle-double-left"></i>
                  </button>
                </li>
                
                <!-- Página anterior -->
                <li class="page-item" :class="{ disabled: pagination.currentPage === 1 || loading }">
                  <button 
                    class="page-link" 
                    @click="goToPreviousPage"
                    :disabled="pagination.currentPage === 1 || loading"
                    title="Página anterior"
                  >
                    <i class="fas fa-angle-left"></i>
                  </button>
                </li>
                
                <!-- Páginas numeradas -->
                <li 
                  v-for="page in availablePages" 
                  :key="page" 
                  class="page-item" 
                  :class="{ active: page === pagination.currentPage, disabled: loading }"
                >
                  <button 
                    class="page-link" 
                    @click="changePage(page)"
                    :disabled="loading"
                  >
                    {{ page }}
                  </button>
                </li>
                
                <!-- Página siguiente -->
                <li class="page-item" :class="{ disabled: pagination.currentPage === totalPages || loading }">
                  <button 
                    class="page-link" 
                    @click="goToNextPage"
                    :disabled="pagination.currentPage === totalPages || loading"
                    title="Página siguiente"
                  >
                    <i class="fas fa-angle-right"></i>
                  </button>
                </li>
                
                <!-- Última página -->
                <li class="page-item" :class="{ disabled: pagination.currentPage === totalPages || loading }">
                  <button 
                    class="page-link" 
                    @click="goToLastPage"
                    :disabled="pagination.currentPage === totalPages || loading"
                    title="Última página"
                  >
                    <i class="fas fa-angle-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
      
      <!-- Indicador cuando no hay registros (solo informativo) -->
      <div v-if="!records || records.length === 0" class="row mt-3">
        <div class="col-12">
          <div class="alert alert-info">
            <h6><i class="fas fa-info-circle me-2"></i>Información de Estado</h6>
            <p class="mb-0">
              📊 <strong>Registros en página actual:</strong> {{ records ? records.length : 0 }}<br>
              📈 <strong>Total registros filtrados:</strong> {{ totalFilteredRecords }}<br>
              📄 <strong>Estado:</strong> {{ totalFilteredRecords > 0 ? 'Datos disponibles, aplicando filtros...' : 'Sin datos para los filtros aplicados' }}
            </p>
          </div>
        </div>
      </div>





      <!-- Acciones en Lote -->
      <div v-if="selectedItemsCount > 0" class="alert alert-primary mb-3">
        <div class="row align-items-center">
          <div class="col-md-6">
            <div class="d-flex align-items-center">
              <div class="me-3">
                <i class="fas fa-check-square fa-lg text-primary"></i>
              </div>
              <div>
                <strong>{{ selectedItemsCount }} elemento(s) seleccionado(s)</strong>
                <br>
                <small class="text-muted">
                  {{ selectedPendingSales }} pendiente(s) • Total: {{ formatCurrency(selectedTotalAmount) }}
                </small>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="btn-group w-100" role="group">
              <button 
                type="button" 
                class="btn btn-info"
                @click="confirmBulkResendNotifications"
                :disabled="!canBulkResendNotifications || isBulkProcessing"
              >
                <i v-if="isBulkProcessing" class="fas fa-spinner fa-spin me-2"></i>
                <i v-else class="fas fa-envelope me-2"></i>
                {{ isBulkProcessing ? 'Enviando...' : `Reenviar Notificaciones` }}
              </button>
              <button 
                type="button" 
                class="btn btn-danger"
                @click="confirmBulkCancel"
                :disabled="!canBulkCancel || isBulkProcessing"
              >
                <i v-if="isBulkProcessing" class="fas fa-spinner fa-spin me-2"></i>
                <i v-else class="fas fa-times-circle me-2"></i>
                {{ isBulkProcessing ? 'Cancelando...' : `Cancelar ${selectedPendingSales}` }}
              </button>
              <button 
                type="button" 
                class="btn btn-success"
                @click="exportSelectedToExcel"
                :disabled="isBulkProcessing"
              >
                <i class="fas fa-file-excel me-2"></i>
                Exportar Selección
              </button>
              <button 
                type="button" 
                class="btn btn-outline-secondary"
                @click="clearSelection"
              >
                <i class="fas fa-times me-2"></i>
                Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>



      <!-- Indicador de búsqueda -->
      <div v-if="isSearching" class="alert alert-info d-flex align-items-center mb-3">
        <div class="spinner-border spinner-border-sm me-2" role="status">
          <span class="visually-hidden">Buscando...</span>
        </div>
        <span>Buscando ventas online...</span>
      </div>


    </div>
    
    <!-- Modal para Guardar Filtro -->
    <div class="modal fade" 
         id="saveFilterModal" 
         tabindex="-1" 
         role="dialog"
         aria-labelledby="saveFilterModalLabel" 
         aria-hidden="true"
         data-bs-backdrop="static"
         style="z-index: 9045 !important;">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="saveFilterModalLabel">
              <i :class="isEditingFilter ? 'fas fa-edit me-2' : 'fas fa-save me-2'"></i>
              {{ isEditingFilter ? 'Editar Filtro Guardado' : 'Guardar Configuración de Filtros' }} - Ventas Online
            </h5>
            <button type="button" class="btn-close" @click="closeSaveFilterModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveCurrentFilter">
              <div class="mb-3">
                <label for="filterName" class="form-label">Nombre del Filtro *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="filterName"
                  v-model="filterToSave.name"
                  placeholder="Ej: Pagos pendientes este mes"
                  required
                  maxlength="50"
                >
                <div class="form-text">Máximo 50 caracteres</div>
              </div>
              <div class="mb-3">
                <label for="filterDescription" class="form-label">Descripción</label>
                <textarea 
                  class="form-control" 
                  id="filterDescription"
                  v-model="filterToSave.description"
                  placeholder="Descripción opcional del filtro..."
                  rows="3"
                  maxlength="200"
                ></textarea>
                <div class="form-text">Máximo 200 caracteres</div>
              </div>
              <div class="alert alert-info">
                <h6 class="alert-heading">
                  <i class="fas fa-info-circle me-2"></i>
                  Configuración a Guardar:
                </h6>
                <ul class="mb-0 small">
                  <li v-if="filters.searchText">Texto: "{{ filters.searchText }}"</li>
                  <li v-if="filters.branch">Sucursal: {{ getBranchName(filters.branch) }}</li>
                  <li v-if="filters.status">Estado: {{ filters.status }}</li>
                  <li v-if="filters.trxType">Tipo: {{ filters.trxType }}</li>
                  <li v-if="filters.platform">Plataforma: {{ filters.platform }}</li>
                  <li v-if="filters.dateFrom">Desde: {{ filters.dateFrom }}</li>
                  <li v-if="filters.dateTo">Hasta: {{ filters.dateTo }}</li>
                  <li v-if="filters.amountFrom">Monto desde: {{ formatCurrency(filters.amountFrom) }}</li>
                  <li v-if="filters.amountTo">Monto hasta: {{ formatCurrency(filters.amountTo) }}</li>
                  <li>Ordenamiento: {{ sorting.field }} ({{ sorting.direction }})</li>
                </ul>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeSaveFilterModal">
              Cancelar
            </button>
            <button type="button" class="btn btn-primary" @click="saveCurrentFilter">
              <i :class="isEditingFilter ? 'fas fa-edit me-2' : 'fas fa-save me-2'"></i>
              {{ isEditingFilter ? 'Actualizar Filtro' : 'Guardar Filtro' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal de Detalles de Venta Online -->
    <OnlineSaleDetailModal 
      :visible="showDetailModal"
      :sale="selectedSaleForDetail"
      @close="closeDetailModal"
    />
  </div>
</template>

<script>
import "perfect-scrollbar/css/perfect-scrollbar.css";
import PerfectScrollbar from "perfect-scrollbar";
import { Modal } from 'bootstrap';
import Chart from 'chart.js';
import {
  Badge,
  Modal as UIModal,
  CollapseItem,
  Collapse,
} from "src/components/UIComponents";
import { Tooltip, DatePicker, Select } from "element-ui";
import OnlineSaleDetailModal from "./OnlineSaleDetailModal.vue";
import chartMixin from '@/mixins/chartMixin';
import excelExportMixin from '@/mixins/excelExportMixin';

// Función debounce para optimizar búsquedas
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default {
  name: 'OnLineSalesTable',
  components: {
    Badge,
    Modal: UIModal,
    CollapseItem,
    Collapse,
    [Tooltip.name]: Tooltip,
    [DatePicker.name]: DatePicker,
    [Select.name]: Select,
    OnlineSaleDetailModal,
  },
  mixins: [chartMixin, excelExportMixin],
  props: {
    statusCatalog: {
      type: Array,
      default: () => {
        return [
          "PENDIENTE",
          "EN CURSO",
          "PAGADO",
          "ERROR",
          "EXPIRADO",
          "FALLO NOTIFICACION",
          "CANCELADO",
          "RECHAZADO",
        ];
      },
    },
    trxTypeCatalog: {
      type: Array,
      default: () => {
        return ["MEMBRESIA", "SERVICIO", "PRODUCTO", "MIXTO", "SUSCRIPCION"];
      },
    },
    branches: {
      type: Array,
      default: () => {
        return [{ _id: "nodata", nombreSucursal: "nodata" }];
      },
    },
    records: {
      type: Array,
      default: () => {
        return [
          {
            _id: "mockId.....",
            commerceOrder: 0,
            estado: "IMAGINARIO",
            fechaCreacion: new Date("01-01-1900"),
            itemVendido: { descripcionPlan: "PLAN IMAGINARIO", valorPlan: 340 },
            linkPago: "",
            nombreSucursal: "Sucursal Imaginaria",
            plataforma: "FLOW",
            responsable: "Hombre Imaginario",
            socio: {
              rut: "9999999-9",
              nombre: "Hombre Imaginario",
              email: "correo.imaginario@centralgym.cl",
            },
            tipoVenta: "IMAGINARIA",
          },
        ];
      },
    },
    loading: {
      type: Boolean,
      default: false
    },
    totalRecords: {
      type: Number,
      default: 0
    },
    // Nuevas props para KPIs correctos basadas en todos los datos filtrados
    totalFilteredRecords: {
      type: Number,
      default: 0
    },
    totalFilteredAmount: {
      type: Number,
      default: 0
    },
    totalPaidSales: {
      type: Number,
      default: 0
    },
    totalPendingSales: {
      type: Number,
      default: 0
    },
    totalErrorSales: {
      type: Number,
      default: 0
    },
    totalSuccessfulNotifications: {
      type: Number,
      default: 0
    },
    // Nuevas props para paginación local
    allFilteredRecords: {
      type: Array,
      default: () => []
    },
    localPagination: {
      type: Object,
      default: () => ({
        currentPage: 1,
        itemsPerPage: 20,
        totalPages: 1
      })
    }
  },
  data() {
    return {
      // Filtros mejorados
      filters: {
        searchText: '',
        branch: '',
        status: '',
        trxType: '',
        commerceOrder: '',
        dateFrom: '',
        dateTo: '',
        amountFrom: '',
        amountTo: '',
        platform: '',
        notificationStatus: ''
      },
      // Estados de carga
      isSearching: false,
      isExporting: false,
      isBulkProcessing: false,
      isResendingNotification: false,
      isCancelling: false,
      isApplyingAdvancedFilters: false,
      // Sistema de paginación
      pagination: {
        currentPage: 1,
        totalPages: 0,
        itemsPerPage: 20,
        totalRecords: 0
      },
      // Sistema de ordenamiento
      sorting: {
        field: 'fechaCreacion',
        direction: 'desc'
      },
      // UI States
      showAdvancedFilters: false,
      showChartsPanel: false,
      showSavedFilters: false,
      // Selección múltiple
      selectedItems: [],
      allSelected: false,
      // Filtros guardados
      savedFilters: [],
      filterToSave: {
        name: '',
        description: ''
      },
      filterToEdit: null, // Filtro que se está editando
      isEditingFilter: false, // Bandera para saber si estamos editando
      // Gráficos
      chartData: {
        salesByStatus: [],
        salesByType: [],
        salesByBranch: [],
        salesByPlatform: [],
        dailyTrend: [],
        notificationStatus: []
      },
      // Instancias de Chart.js
      chartInstances: {
        statusChart: null,
        typeChart: null,
        branchChart: null,
        platformChart: null,
        trendChart: null,
        notificationChart: null
      },
      // Modal instances
      saveFilterModal: null,
      // Modal de detalles
      showDetailModal: false,
      selectedSaleForDetail: null,
      // Compatibilidad con versión anterior
      showMobileFilters: false,
      selectedStatus: null,
      selectedTrxType: null,
      selectedBranch: null,

      commerceOrderFilter: null,
      currentPage: 0,
      keepFetching: true,
    };
  },
  computed: {
    // Calcular totales de paginación
    totalPages() {
      // Usar totalRecords prop que debería contener el total de registros filtrados
      const totalRecords = this.totalRecords || 0;
      return Math.ceil(totalRecords / this.pagination.itemsPerPage);
    },
    // Páginas disponibles para mostrar en paginador
    availablePages() {
      const pages = [];
      const maxPages = 5; // Mostrar máximo 5 páginas
      const current = this.pagination.currentPage;
      const total = this.totalPages;
      
      let start = Math.max(1, current - Math.floor(maxPages / 2));
      let end = Math.min(total, start + maxPages - 1);
      
      // Ajustar el inicio si estamos cerca del final
      if (end - start < maxPages - 1) {
        start = Math.max(1, end - maxPages + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    },
    // Información de registros mostrados
    recordsInfo() {
      if (!this.records || this.records.length === 0) {
        return 'No hay registros para mostrar';
      }
      
      const totalRecords = this.totalRecords || 0;
      const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage + 1;
      const end = Math.min(this.pagination.currentPage * this.pagination.itemsPerPage, totalRecords);
      return `Mostrando ${start} a ${end} de ${totalRecords} registros`;
    },
    // Estadísticas calculadas para ventas online
    totalOnlineSales() {
      // Usar la prop totalFilteredRecords que contiene todos los registros filtrados
      return this.totalFilteredRecords || 0;
    },
    totalAmount() {
      // Usar la prop totalFilteredAmount que contiene el monto total filtrado
      return this.totalFilteredAmount || 0;
    },
    paidSales() {
      // Usar la prop totalPaidSales que contiene las ventas pagadas filtradas
      return this.totalPaidSales || 0;
    },
    pendingSales() {
      // Usar la prop totalPendingSales que contiene las ventas pendientes filtradas
      return this.totalPendingSales || 0;
    },
    errorSales() {
      // Usar la prop totalErrorSales que contiene las ventas con error filtradas
      return this.totalErrorSales || 0;
    },
    averageSaleAmount() {
      if (this.totalOnlineSales === 0) return 0;
      return this.totalAmount / this.totalOnlineSales;
    },
    successPercentage() {
      if (this.totalOnlineSales === 0) return 0;
      return Math.round((this.paidSales / this.totalOnlineSales) * 100);
    },
    notificationSuccessPercentage() {
      if (this.totalOnlineSales === 0) return 0;
      // Usar la prop totalSuccessfulNotifications
      return Math.round((this.totalSuccessfulNotifications / this.totalOnlineSales) * 100);
    },
    // Selección múltiple
    selectedItemsCount() {
      return this.selectedItems.length;
    },
    selectedPendingSales() {
      return this.selectedItems.filter(item => {
        const sale = this.records.find(record => record._id === item);
        return sale && ['PENDIENTE', 'EN CURSO'].includes(sale.estado);
      }).length;
    },
    selectedTotalAmount() {
      return this.selectedItems.reduce((sum, itemId) => {
        const sale = this.records.find(record => record._id === itemId);
        return sum + (this.parseMonetaryValue(sale?.itemVendido?.valorPlan) || 0);
      }, 0);
    },
    canBulkCancel() {
      return this.selectedPendingSales > 0;
    },
    canBulkResendNotifications() {
      return this.selectedItemsCount > 0;
    },
    // Filtros guardados ordenados
    sortedSavedFilters() {
      return this.savedFilters.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },
    
    // Detectar si hay filtros avanzados aplicados
    hasAdvancedFiltersApplied() {
      return this.filters.searchText || 
             this.filters.platform || 
             this.filters.amountFrom || 
             this.filters.amountTo || 
             this.filters.notificationStatus;
    },
    
    // Título dinámico para gráficos con rango de fechas
    tituloAnalisisVisual() {
      const rangoFechas = this.obtenerRangoFechasParaTitulo();
      return rangoFechas 
        ? `ANÁLISIS VISUAL DE VENTAS ONLINE - ${rangoFechas}`
        : 'ANÁLISIS VISUAL DE VENTAS ONLINE';
    }
  },
  methods: {
    async copyLink(link) {
      // this.$logger.log(link);
      await navigator.clipboard.writeText(link).then(() => {
        this.$swal.fire({
          title: "Copiado",
          text: "El enlace de pago ha sido copiado al portapapeles.",
          icon: "success",
          confirmButtonColor: "#6bd098", // Color personalizado del botón "OK"
          confirmButtonText: "OK",
        });
      });
    },
    
    // ========================================
    // MÉTODO AUXILIAR PARA FORMATEO DE MONTOS EN GRÁFICOS
    // ========================================
    parseMonetaryValue(value) {
      if (typeof value === 'number') return value;
      if (!value) return 0;
      
      // Convertir a string y limpiar espacios
      let stringValue = String(value).trim();
      
      // Remover símbolo de moneda si existe
      stringValue = stringValue.replace(/[$]/g, '');
      
      // Si el valor contiene puntos y comas, asumir formato chileno (1.500.000,50)
      if (stringValue.includes('.') && stringValue.includes(',')) {
        // Formato chileno: punto para miles, coma para decimales
        stringValue = stringValue.replace(/\./g, '').replace(',', '.');
      } else if (stringValue.includes('.') && !stringValue.includes(',')) {
        // Verificar si es formato chileno sin decimales (1.500.000) o formato US (1500.50)
        const parts = stringValue.split('.');
        if (parts.length === 2 && parts[1].length > 2) {
          // Probablemente formato chileno (1.500.000)
          stringValue = stringValue.replace(/\./g, '');
        }
        // Si tiene 2 decimales o menos, asumir formato US
      } else if (stringValue.includes(',') && !stringValue.includes('.')) {
        // Solo comas, puede ser formato US (1,500) o chileno con decimales (1500,50)
        const parts = stringValue.split(',');
        if (parts.length === 2 && parts[1].length <= 2) {
          // Formato chileno con decimales (1500,50)
          stringValue = stringValue.replace(',', '.');
        } else {
          // Formato US con separador de miles (1,500)
          stringValue = stringValue.replace(/,/g, '');
        }
      }
      
      const parsed = parseFloat(stringValue);
      return isNaN(parsed) ? 0 : parsed;
    },
    
    formatCurrencyForCharts(amount) {
      if (!amount || isNaN(amount)) return '$0';
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    },
    
    // ========================================
    // SCROLL INFINITO (DESHABILITADO CON PAGINACIÓN LOCAL)
    // ========================================
    handleScroll(event) {
      // SCROLL INFINITO DESHABILITADO: Con paginación local no necesitamos scroll infinito
      // El sistema ahora carga todos los datos filtrados de una vez y usa paginación local
      this.$logger.log('⚠️ Scroll infinito deshabilitado - usando paginación local');
      return;
      
      // CÓDIGO ANTERIOR (comentado):
      /*
      const { target } = event;
      if (
        target.scrollHeight - target.scrollTop === target.clientHeight &&
        this.keepFetching
      ) {
        this.currentPage++;
        this.fetchNewPage();
      }
      */
    },
    toggleMobileFilters(val) {
      this.showMobileFilters = !!val;
    },
    // Sincronizar filtros antiguos con los nuevos
    syncFiltersFromOldToNew() {
      // Sincronizar desde los filtros antiguos a los nuevos
      this.filters.status = this.selectedStatus || '';
      this.filters.trxType = this.selectedTrxType || '';
      this.filters.branch = this.selectedBranch || '';
      this.filters.commerceOrder = this.commerceOrderFilter || '';
      // Las fechas ya están sincronizadas directamente en filters.dateFrom/dateTo
    },
    
    // Sincronizar filtros nuevos con los antiguos
    syncFiltersFromNewToOld() {
      // Sincronizar desde los filtros nuevos a los antiguos
      this.selectedStatus = this.filters.status || null;
      this.selectedTrxType = this.filters.trxType || null;
      this.selectedBranch = this.filters.branch || null;
      this.commerceOrderFilter = this.filters.commerceOrder || null;
      // Las fechas ya están sincronizadas directamente desde filters.dateFrom/dateTo
    },
    
    // Validación de rango de fechas (frontend)
    validateDateRange(dateFrom, dateTo) {
      if (!dateFrom || !dateTo) return { valid: true }; // Sin fechas = válido
      
      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);
      const diffTime = Math.abs(toDate - fromDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const maxDays = 365; // 1 año máximo
      
      if (diffDays > maxDays) {
        return {
          valid: false,
          message: `El rango de fechas no puede ser mayor a 1 año. Rango actual: ${diffDays} días.`
        };
      }
      
      return { valid: true };
    },
    
    // Método unificado de búsqueda
    performUnifiedSearch() {
      // Sincronizar filtros antes de buscar
      this.syncFiltersFromNewToOld();
      
      // Validar rango de fechas antes de enviar
      const dateFrom = this.filters.dateFrom || null;
      const dateTo = this.filters.dateTo || null;
      
      if (dateFrom && dateTo) {
        const validation = this.validateDateRange(dateFrom, dateTo);
        if (!validation.valid) {
          this.$swal.fire({
            icon: "warning",
            title: "Rango de fechas no válido",
            text: validation.message,
            confirmButtonColor: "#6bd098",
          });
          return;
        }
      }
      
      // Determinar qué tipo de búsqueda realizar
      const searchParams = {
        // Filtros básicos (SOPORTADOS por endpoint actual)
        status: this.selectedStatus,
        trxType: this.selectedTrxType,
        branch: this.selectedBranch,
        dateFrom: dateFrom,
        dateTo: dateTo,
        commerceOrder: isNaN(`${this.commerceOrderFilter}`) ? null : parseInt(this.commerceOrderFilter),
        
        // Filtros avanzados (FUNCIONAN localmente)
        searchText: this.filters.searchText,
        platform: this.filters.platform,
        amountFrom: this.filters.amountFrom,
        amountTo: this.filters.amountTo,
        notificationStatus: this.filters.notificationStatus,
        
        // Paginación
        page: this.pagination.currentPage - 1, // Convertir a base 0 para compatibilidad
        itemsPerPage: this.pagination.itemsPerPage,
        
        // Ordenamiento (PREPARADO para futuro backend)
        // NOTA: El endpoint actual no soporta ordenamiento dinámico
        sortField: this.sorting.field,
        sortDirection: this.sorting.direction
      };
      
      // Emitir evento de búsqueda unificada
      this.$emit('searchUnified', searchParams);
    },
    
    // Método de búsqueda principal (actualizado)
    search() {
      // Sincronizar filtros
      this.syncFiltersFromOldToNew();
      
      // Resetear paginación
      this.pagination.currentPage = 1;
      this.currentPage = 0; // Para compatibilidad con sistema anterior
      this.keepFetching = true;
      
      // Realizar búsqueda unificada
      this.performUnifiedSearch();
    },
    exportRecords() {
      // ✅ ACTIVAR SPINNER: Mostrar ícono de loading al usuario
      this.isExporting = true;
      
      this.$logger.log('📊 Iniciando exportación ventas online - Spinner activado');
      
      this.$emit(
        "export",
        this.selectedStatus,
        this.selectedTrxType,
        this.selectedBranch,
        this.filters.dateFrom,
        this.filters.dateTo || null,
        isNaN(`${this.commerceOrderFilter}`)
          ? `not a number`
          : parseInt(this.commerceOrderFilter)
      );
      
      this.$logger.log('📊 Exportación ventas online solicitada:', {
        filtros: {
          status: this.selectedStatus,
          trxType: this.selectedTrxType,
          branch: this.selectedBranch,
          fechaDesde: this.filters.dateFrom,
          fechaHasta: this.filters.dateTo,
          commerceOrder: this.commerceOrderFilter
        },
        registrosEnPagina: this.records ? this.records.length : 0,
        metodo: 'evento export → dashboard'
      });
      
      // ⏰ DESACTIVAR SPINNER: Dar tiempo al usuario de ver el spinner y luego que el dashboard lo maneje
      setTimeout(() => {
        this.isExporting = false;
        this.$logger.log('📊 Exportación delegada al dashboard - Spinner desactivado');
      }, 500); // 500ms para que el usuario vea el feedback visual
    },
    // ========================================
    // FETCH NEW PAGE (DESHABILITADO CON PAGINACIÓN LOCAL)
    // ========================================
    fetchNewPage() {
      // FETCHNEWPAGE DESHABILITADO: Con paginación local no necesitamos obtener nuevas páginas del endpoint
      this.$logger.log('⚠️ fetchNewPage deshabilitado - usando paginación local');
      return;
      
      // CÓDIGO ANTERIOR (comentado):
      /*
      if (this.keepFetching) {
        this.currentPage++;
        this.pagination.currentPage = this.currentPage + 1;
        
        this.performUnifiedSearch();
        
        this.$emit(
          "fetchNewPage",
          this.currentPage,
          this.selectedStatus,
          this.selectedTrxType,
          this.selectedBranch,
          this.dateFilter && this.dateFilter.length > 0 ? this.dateFilter[0] : null,
          this.dateFilter && this.dateFilter.length > 1 ? this.dateFilter[1] : null,
          isNaN(`${this.commerceOrderFilter}`)
            ? `not a number`
            : parseInt(this.commerceOrderFilter)
        );
      }
      */
    },
    stopFetching() {
      this.keepFetching = false;
    },
    refresh() {
      // Limpiar todos los filtros
      this.clearAllFilters();
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('es-CL');
    },
    formatTime(dateString) {
      return new Date(dateString).toLocaleTimeString('es-CL', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP'
      }).format(value);
    },
    getCurrentDateTime() {
      return new Date().toLocaleString('es-CL');
    },
    getTodayDate() {
      return new Date().toISOString().split('T')[0];
    },
    getStatusBadgeClass(status) {
      const classMap = {
        'PAGADO': 'badge bg-success',
        'PENDIENTE': 'badge bg-warning text-dark',
        'EN CURSO': 'badge bg-info text-dark',
        'ERROR': 'badge bg-danger',
        'CANCELADO': 'badge bg-secondary',
        'RECHAZADO': 'badge bg-danger',
        'EXPIRADO': 'badge bg-dark',
        'FALLO NOTIFICACION': 'badge bg-warning text-dark'
      };
      return classMap[status] || 'badge bg-secondary';
    },
    getNotificationCreationClass(record) {
      if (!record.socio.notificacionCreacion) {
        return 'text-secondary';
      }
      return record.socio.notificacionCreacion.accepted ? 'text-success' : 'text-danger';
    },
    getNotificationCreationTooltip(record) {
      if (!record.socio.notificacionCreacion) {
        return 'No se ha enviado notificación de la creación del link';
      }
      return record.socio.notificacionCreacion.accepted 
        ? 'El socio fue notificado de la creación del link'
        : 'Hubo un error al intentar notificar al socio de la creación de su link de pago';
    },
    getNotificationPaymentClass(record) {
      if (record.estado === 'RECHAZADO') {
        return 'text-warning';
      }
      return record.socio.notificacionPagoExitoso?.accepted ? 'text-success' : 'text-danger';
    },
    getNotificationPaymentTooltip(record) {
      if (record.estado === 'RECHAZADO') {
        return 'Se notificó al socio que el pago fue rechazado por la pasarela de pago';
      }
      return record.socio.notificacionPagoExitoso?.accepted
        ? 'El socio fue notificado exitosamente sobre el pago realizado'
        : 'Ocurrió un error al notificar al socio que su pago se realizó exitosamente';
    },
    handleSearch() {
      this.isSearching = true;
      this.pagination.currentPage = 1; // Resetear a primera página
      this.debouncedSearch();
    },
    performSearch() {
      this.performUnifiedSearch();
      
      // El loading se desactiva cuando el componente padre termine de cargar
      setTimeout(() => {
        this.isSearching = false;
      }, 300);
    },
    refreshData() {
      this.search(); // Usar método de búsqueda unificado
    },
    clearAllFilters() {
      // Limpiar todos los filtros (nuevos y antiguos)
      this.filters = {
        searchText: '',
        branch: '',
        status: '',
        trxType: '',
        commerceOrder: '',
        dateFrom: '',
        dateTo: '',
        amountFrom: '',
        amountTo: '',
        platform: '',
        notificationStatus: ''
      };
      
      // Limpiar filtros antiguos
      this.selectedStatus = null;
      this.selectedTrxType = null;
      this.selectedBranch = null;
      this.commerceOrderFilter = null;
      
      // Resetear paginación
      this.pagination.currentPage = 1;
      this.currentPage = 0;
      this.keepFetching = true;
      
      // Realizar búsqueda
      this.performUnifiedSearch();
    },
    setDateFilter(period) {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      let dateFrom, dateTo;
      
      switch (period) {
        case 'today':
          dateFrom = this.getTodayDate();
          dateTo = this.getTodayDate();
          break;
        case 'yesterday':
          const yesterdayStr = yesterday.toISOString().split('T')[0];
          dateFrom = yesterdayStr;
          dateTo = yesterdayStr;
          break;
        case 'week':
          const weekStart = new Date(today);
          // Configurar para que la semana inicie en lunes (no domingo)
          // getDay(): 0=domingo, 1=lunes, 2=martes, etc.
          // Si es domingo (0), necesitamos ir 6 días atrás para llegar al lunes
          // Si es lunes (1), necesitamos ir 0 días atrás
          // Si es martes (2), necesitamos ir 1 día atrás, etc.
          const daysFromMonday = (today.getDay() + 6) % 7; // Convierte domingo a 6, lunes a 0, etc.
          weekStart.setDate(today.getDate() - daysFromMonday);
          dateFrom = weekStart.toISOString().split('T')[0];
          dateTo = this.getTodayDate();
          break;
        case 'month':
          const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
          dateFrom = monthStart.toISOString().split('T')[0];
          dateTo = this.getTodayDate();
          break;
      }
      
      this.$logger.log(`📅 Aplicando filtro de fecha rápido (${period}):`, { dateFrom, dateTo });
      
      // NUEVO: Aplicar filtro de fecha LOCALMENTE sobre datos en memoria
      this.applyLocalDateFilter(dateFrom, dateTo, period);
    },
    clearDateFilters() {
      this.$logger.log('🗓️ Limpiando filtros de fecha');
      
      // Limpiar filtros de UI
      this.filters.dateFrom = '';
      this.filters.dateTo = '';
      
      // Sincronizar con filtros antiguos
      this.syncFiltersFromNewToOld();
      
      // NUEVO: Aplicar limpieza de fecha localmente (mantener filtros avanzados)
      this.applyDateFilterLocally('', '', 'clear');
    },
    
    // ========================================
    // MÉTODOS DE FILTROS AVANZADOS
    // ========================================
    handleAdvancedFilterChange() {
      // Mostrar indicador de que se están aplicando filtros
      this.isApplyingAdvancedFilters = true;
      
      // Aplicar debounce automáticamente para mejorar UX
      this.$logger.log('🔄 Filtro avanzado cambiado, aplicando en 1 segundo...');
      if (this.debouncedAdvancedFilter) {
        this.debouncedAdvancedFilter();
      } else {
        // Fallback si no existe el debounce
        this.$logger.warn('⚠️ debouncedAdvancedFilter no existe, aplicando directamente');
        this.applyAdvancedFilters();
      }
    },
    
    applyAdvancedFilters() {
      // Emitir evento al componente padre para aplicar filtros localmente
      const advancedFilters = {
        searchText: this.filters.searchText,
        platform: this.filters.platform,
        amountFrom: this.filters.amountFrom,
        amountTo: this.filters.amountTo,
        notificationStatus: this.filters.notificationStatus
      };
      
      this.$logger.log('🔧 Aplicando filtros avanzados:', advancedFilters);
      this.$emit('applyAdvancedFilters', advancedFilters);
      
      // Ocultar indicador después de un breve delay para que el usuario vea que se aplicó
      setTimeout(() => {
        this.isApplyingAdvancedFilters = false;
      }, 300);
    },
    
    clearAdvancedFilters() {
      // Limpiar solo los filtros avanzados (sin hacer nueva búsqueda a BD)
      this.filters.searchText = '';
      this.filters.platform = '';
      this.filters.amountFrom = '';
      this.filters.amountTo = '';
      this.filters.notificationStatus = '';
      
      // Ocultar indicador de filtrado
      this.isApplyingAdvancedFilters = false;
      
      this.$logger.log('🔄 Limpiando filtros avanzados - Solo trabajando en memoria');
      
      // CORREGIDO: NO emitir evento para evitar nueva búsqueda a BD
      // En su lugar, aplicar filtros localmente para restaurar datos originales
      if (this.allFilteredRecords && this.allFilteredRecords.length > 0) {
        this.$logger.log('📊 Restaurando datos originales en memoria sin consultar servidor');
        
        // Aplicar limpieza de filtros localmente manteniendo filtros de fecha si existen
        const dateFilterParams = {
          isLocalDateFilter: false, // Indicar que es limpieza de filtros avanzados
          dateFrom: this.filters.dateFrom,
          dateTo: this.filters.dateTo, 
          period: 'clearAdvanced', // Período especial para identificar limpieza
          // Filtros avanzados vacíos después de limpiar
          advancedFilters: {
            searchText: '',
            platform: '',
            amountFrom: '',
            amountTo: '',
            notificationStatus: ''
          }
        };
        
        // Aplicar filtro local para restaurar datos originales
        this.$emit('applyLocalDateFilter', dateFilterParams);
      } else {
        this.$logger.log('⚠️ No hay datos en memoria - se requiere nueva búsqueda');
        // Solo en este caso sí emitir para obtener datos
        this.$emit('clearAdvancedFilters');
      }
      
      this.$logger.log('✅ Filtros avanzados limpiados - Trabajando solo en memoria');
    },
    
    // ========================================
    // MÉTODOS DE FILTROS DE FECHA LOCALES
    // ========================================
    applyLocalDateFilter(dateFrom, dateTo, period) {
      // Actualizar filtros en UI
      this.filters.dateFrom = dateFrom;
      this.filters.dateTo = dateTo;
      
      // Sincronizar con filtros antiguos
      this.syncFiltersFromNewToOld();
      
      // Aplicar filtro de fecha sobre datos originales (sin filtros avanzados)
      // y luego aplicar filtros avanzados si existen
      this.applyDateFilterLocally(dateFrom, dateTo, period);
    },
    
    applyDateFilterLocally(dateFrom, dateTo, period) {
      // Emitir evento especial para filtro de fecha local
      const dateFilterParams = {
        isLocalDateFilter: true,
        dateFrom: dateFrom,
        dateTo: dateTo,
        period: period,
        // Incluir filtros avanzados actuales para mantenerlos
        advancedFilters: {
          searchText: this.filters.searchText,
          platform: this.filters.platform,
          amountFrom: this.filters.amountFrom,
          amountTo: this.filters.amountTo,
          notificationStatus: this.filters.notificationStatus
        }
      };
      
      this.$logger.log('📅 Aplicando filtro de fecha local:', dateFilterParams);
      this.$emit('applyLocalDateFilter', dateFilterParams);
    },
    
    // ========================================
    // MÉTODOS DE PAGINACIÓN LOCAL (ACTUALIZADOS)
    // ========================================
    changePage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.pagination.currentPage) {
        this.pagination.currentPage = page;
        this.currentPage = page - 1; // Sincronizar con sistema anterior (base 0)
        
        // NUEVO: Usar paginación local en lugar de consultar endpoint
        this.performLocalPageChange(page);
      }
    },
    goToFirstPage() {
      this.changePage(1);
    },
    goToLastPage() {
      this.changePage(this.totalPages);
    },
    goToPreviousPage() {
      this.changePage(this.pagination.currentPage - 1);
    },
    goToNextPage() {
      this.changePage(this.pagination.currentPage + 1);
    },
    changeItemsPerPage(newSize) {
      this.pagination.itemsPerPage = parseInt(newSize);
      this.pagination.currentPage = 1; // Resetear a primera página
      this.currentPage = 0; // Sincronizar con sistema anterior
      
      // NUEVO: Usar paginación local para cambio de items per page
      this.performLocalPageChange(1, parseInt(newSize));
    },
    
    // ========================================
    // MÉTODO NUEVO: PAGINACIÓN LOCAL
    // ========================================
    performLocalPageChange(page, itemsPerPage = null) {
      const searchParams = {
        isLocalPageChange: true, // Flag para indicar que es cambio de página local
        page: page - 1, // Convertir a base 0 para compatibilidad
        itemsPerPage: itemsPerPage || this.pagination.itemsPerPage,
        // No incluir filtros porque no es una nueva búsqueda
      };
      
      this.$logger.log('📄 Cambio de página local:', {
        page: page,
        itemsPerPage: searchParams.itemsPerPage,
        totalRegistrosFiltrados: this.allFilteredRecords.length
      });
      
      // Emitir evento de búsqueda unificada con flag local
      this.$emit('searchUnified', searchParams);
    },
    
    // ========================================
    // MÉTODOS DE ORDENAMIENTO (ACTUALIZADOS)
    // ========================================
    sortBy(field) {
      this.$logger.log('🔧 OnLineSalesTable.sortBy:', field);
      
      if (this.sorting.field === field) {
        this.sorting.direction = this.sorting.direction === 'asc' ? 'desc' : 'asc';
      } else {
        this.sorting.field = field;
        this.sorting.direction = 'asc';
      }
      
      this.$logger.log('📊 Nuevo ordenamiento:', this.sorting.field, this.sorting.direction);
      
      // ✅ CORREGIDO: Ordenar en memoria en lugar de consultar API
      this.applySortingLocally();
    },
    
    // ========================================
    // MÉTODO NUEVO: APLICAR ORDENAMIENTO LOCAL
    // ========================================
    applySortingLocally() {
      // Verificar que tenemos datos para ordenar
      if (!this.allFilteredRecords || this.allFilteredRecords.length === 0) {
        this.$logger.warn('⚠️ No hay datos filtrados para ordenar (OnLineSales)');
        return;
      }
      
      this.$logger.log('🔧 Aplicando ordenamiento local:', this.sorting.field, this.sorting.direction);
      
      // Crear copia de los datos para ordenar
      const sortedData = [...this.allFilteredRecords];
      
      // Aplicar ordenamiento
      const sortedResult = this.sortDataLocally(sortedData, this.sorting.field, this.sorting.direction);
      
      // ✅ CORREGIDO: NO mutar la prop directamente, sino emitir evento al padre
      // this.allFilteredRecords = sortedResult; // ❌ ESTO CAUSABA EL ERROR
      
      // Resetear a primera página después del ordenamiento para mantener consistencia
      this.pagination.currentPage = 1;
      this.currentPage = 0;
      
      // Emitir evento específico para que el padre actualice allFilteredRecords con los datos ordenados
      const searchParams = {
        isLocalPageChange: true, // Flag para indicar cambio local (ordenamiento)
        isSortingChange: true,   // Flag específico para indicar que es ordenamiento
        sortedData: sortedResult, // ✅ NUEVO: Enviar datos ordenados al padre
        page: 0, // Primera página en base 0
        itemsPerPage: this.pagination.itemsPerPage,
        sortField: this.sorting.field,
        sortDirection: this.sorting.direction
      };
      
      this.$logger.log('✅ Ordenamiento aplicado:', sortedResult.length, 'registros');
      
      // Emitir evento para que el componente padre actualice allFilteredRecords
      this.$emit('searchUnified', searchParams);
    },
    
    getSortClass(field) {
      return {
        'sort-active': this.sorting.field === field,
        'sort-asc': this.sorting.field === field && this.sorting.direction === 'asc',
        'sort-desc': this.sorting.field === field && this.sorting.direction === 'desc'
      };
    },
    getSortIcon(field) {
      if (this.sorting.field !== field) {
        return 'fa-sort text-muted';
      }
      return this.sorting.direction === 'asc' ? 'fa-sort-up text-primary' : 'fa-sort-down text-primary';
    },
    
    // ========================================
    // MÉTODOS DE SELECCIÓN MÚLTIPLE
    // ========================================
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedItems = [];
      } else {
        this.selectedItems = this.records.map(record => record._id);
      }
    },
    updateAllSelectedState() {
      if (!this.records || this.records.length === 0) {
        this.allSelected = false;
        return;
      }
      const allIds = this.records.map(record => record._id);
      this.allSelected = allIds.length > 0 && allIds.every(id => this.selectedItems.includes(id));
    },
    clearSelection() {
      this.selectedItems = [];
    },
    
    // ========================================
    // MÉTODOS DE ACCIONES INDIVIDUALES
    // ========================================
    showDetails(record) {
      this.$logger.log('🔍 Mostrando detalles completos de venta online:', record);
      this.selectedSaleForDetail = record;
      this.showDetailModal = true;
    },
    
    // Método para cerrar el modal de detalles
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedSaleForDetail = null;
    },
    
    canResendNotification(record) {
      // Puede reenviar si está pendiente o si falló la notificación
      return ['PENDIENTE', 'EN CURSO', 'FALLO NOTIFICACION'].includes(record.estado) ||
             (!record.socio.notificacionCreacion?.accepted);
    },
    
    async resendNotification(record) {
      try {
        this.isResendingNotification = true;
        
        // Simulación para desarrollo
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        this.$swal.fire({
          icon: 'success',
          title: '¡Notificación reenviada!',
          text: `Se ha reenviado la notificación a ${record.socio.email}`,
          confirmButtonColor: '#6bd098'
        });
        
        // TODO: Implementar llamada real a la API
        // const response = await this.$api.resendNotification(record._id);
        
      } catch (error) {
        this.$logger.error('Error al reenviar notificación:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error al reenviar',
          text: 'No se pudo reenviar la notificación. Por favor, intenta nuevamente.',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.isResendingNotification = false;
      }
    },
    
    canCancelSale(record) {
      // Solo puede cancelar ventas pendientes o en curso
      return ['PENDIENTE', 'EN CURSO'].includes(record.estado);
    },
    
    confirmCancelSale(record) {
      this.$swal.fire({
        title: '¿Confirmar cancelación?',
        text: `¿Estás seguro de cancelar la venta #${record.commerceOrder}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, cancelar',
        cancelButtonText: 'No cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          this.cancelSale(record);
        }
      });
    },
    
    async cancelSale(record) {
      try {
        this.isCancelling = true;
        
        this.$swal.fire({
          title: "Cancelando venta...",
          text: "Por favor espere",
          showConfirmButton: false,
          allowOutsideClick: false,
          didOpen: () => {
            this.$swal.showLoading();
          },
        });
        
        // Simulación para desarrollo
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Actualizar estado en local
        const saleIndex = this.records.findIndex(r => r._id === record._id);
        if (saleIndex !== -1) {
          this.records[saleIndex].estado = 'CANCELADO';
        }
        
        this.$swal.fire({
          icon: 'success',
          title: '¡Venta cancelada!',
          text: `La venta #${record.commerceOrder} ha sido cancelada exitosamente`,
          confirmButtonColor: '#6bd098'
        });
        
        // TODO: Implementar llamada real a la API
        // const response = await this.$api.cancelSale(record._id);
        
      } catch (error) {
        this.$logger.error('Error al cancelar venta:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error al cancelar',
          text: 'No se pudo cancelar la venta. Por favor, intenta nuevamente.',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.isCancelling = false;
      }
    },
    
    // ========================================
    // MÉTODOS DE ACCIONES EN LOTE
    // ========================================
    async confirmBulkResendNotifications() {
      const result = await this.$swal.fire({
        title: '¿Reenviar notificaciones?',
        text: `¿Deseas reenviar notificaciones a ${this.selectedItemsCount} venta(s) seleccionada(s)?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#17a2b8',
        cancelButtonColor: '#6c757d',
        confirmButtonText: `Sí, reenviar ${this.selectedItemsCount}`,
        cancelButtonText: 'Cancelar'
      });

      if (result.isConfirmed) {
        await this.bulkResendNotifications();
      }
    },
    
    async bulkResendNotifications() {
      try {
        this.isBulkProcessing = true;
        
        this.$swal.fire({
          title: "Reenviando notificaciones...",
          text: `Procesando ${this.selectedItemsCount} notificación(es)`,
          showConfirmButton: false,
          allowOutsideClick: false,
          didOpen: () => {
            this.$swal.showLoading();
          },
        });
        
        // Simulación para desarrollo
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        this.$swal.fire({
          icon: "success",
          title: "¡Notificaciones enviadas!",
          text: `${this.selectedItemsCount} notificación(es) reenviada(s) correctamente`,
          confirmButtonColor: "#6bd098",
        });
        
        // Limpiar selección
        this.clearSelection();
        
      } catch (error) {
        this.$logger.error('Error en reenvío masivo:', error);
        this.$swal.fire({
          icon: "error",
          title: "Error en reenvío masivo",
          text: "No se pudieron reenviar algunas notificaciones. Por favor, intenta nuevamente.",
          confirmButtonColor: "#6bd098",
        });
      } finally {
        this.isBulkProcessing = false;
      }
    },
    
    async confirmBulkCancel() {
      const result = await this.$swal.fire({
        title: '¿Cancelar ventas?',
        text: `¿Deseas cancelar ${this.selectedPendingSales} venta(s) pendiente(s) seleccionada(s)?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: `Sí, cancelar ${this.selectedPendingSales}`,
        cancelButtonText: 'No cancelar'
      });

      if (result.isConfirmed) {
        await this.bulkCancelSales();
      }
    },
    
    async bulkCancelSales() {
      try {
        this.isBulkProcessing = true;
        
        // Obtener IDs de ventas pendientes seleccionadas
        const pendingSalesToCancel = this.selectedItems.filter(itemId => {
          const sale = this.records.find(record => record._id === itemId);
          return sale && ['PENDIENTE', 'EN CURSO'].includes(sale.estado);
        });

        this.$swal.fire({
          title: "Cancelando ventas...",
          text: `Procesando ${pendingSalesToCancel.length} venta(s)`,
          showConfirmButton: false,
          allowOutsideClick: false,
          didOpen: () => {
            this.$swal.showLoading();
          },
        });

        // Simulación para desarrollo
        await new Promise(resolve => setTimeout(resolve, 2500));
        
        // Actualizar estado en los registros
        pendingSalesToCancel.forEach(saleId => {
          const saleIndex = this.records.findIndex(record => record._id === saleId);
          if (saleIndex !== -1) {
            this.records[saleIndex].estado = 'CANCELADO';
          }
        });

        this.$swal.fire({
          icon: "success",
          title: "¡Ventas canceladas!",
          text: `${pendingSalesToCancel.length} venta(s) cancelada(s) correctamente`,
          confirmButtonColor: "#6bd098",
        });

        // Limpiar selección
        this.clearSelection();

      } catch (error) {
        this.$logger.error('Error en cancelación masiva:', error);
        this.$swal.fire({
          icon: "error",
          title: "Error en cancelación masiva",
          text: "No se pudieron cancelar algunas ventas. Por favor, intenta nuevamente.",
          confirmButtonColor: "#6bd098",
        });
      } finally {
        this.isBulkProcessing = false;
      }
    },
    
    // ========================================
    // MÉTODOS DE GRÁFICOS (ACTUALIZADOS PARA USAR TODOS LOS DATOS FILTRADOS)
    // ========================================
    // OPTIMIZADO v3.0: Método updateChartData reemplazado por versión asíncrona
    // Este método ahora procesa los datos sin bloquear el navegador
    updateChartDataOriginal() {
      // Método original conservado para referencia
      // Ahora se usa updateChartDataAsync() del mixin
    },
    
    updateChartData() {
      // Usar la versión asíncrona personalizada para OnLineSales
      this.updateChartDataAsyncOnline();
    },
    
    // Método asíncrono personalizado para OnLineSales
    async updateChartDataAsyncOnline() {
      // Evitar actualizaciones múltiples concurrentes
      if (this.isProcessingCharts) {
        this.$logger.log('⚠️ Ya hay un procesamiento de gráficos en curso');
        return;
      }

      this.isProcessingCharts = true;
      
      const dataSource = this.allFilteredRecords && this.allFilteredRecords.length > 0 
        ? this.allFilteredRecords 
        : this.records;

      if (!dataSource || dataSource.length === 0) {
        this.$logger.log('📊 No hay datos para gráficos (OnLineSales)');
        this.resetChartDataOnline();
        this.isProcessingCharts = false;
        return;
      }

      this.$logger.log(`📊 Iniciando procesamiento asíncrono de gráficos OnLine con ${dataSource.length} registros`);

      try {
        // Procesar todos los gráficos en paralelo
        await Promise.all([
          this.updateStatusChartAsyncOnline(dataSource),
          this.updateTypeChartAsync(dataSource),
          this.updateBranchChartAsyncOnline(dataSource),
          this.updatePlatformChartAsync(dataSource),
          this.updateDailyTrendAsync(dataSource),
          this.updateNotificationChartAsync(dataSource)
        ]);

        this.$logger.log('✅ Todos los gráficos OnLine actualizados exitosamente');
        
        // Renderizar los gráficos después de actualizar los datos
        this.$nextTick(() => {
          this.createStatusChart();
          this.createTypeChart();
          this.createBranchChart();
          this.createPlatformChart();
          this.createTrendChart();
          this.createNotificationChart();
        });

      } catch (error) {
        this.$logger.error('❌ Error al actualizar gráficos OnLine:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error en gráficos',
          text: 'Hubo un problema al generar los gráficos. Por favor, intente nuevamente.',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.isProcessingCharts = false;
      }
    },
    
    // Resetear datos de gráficos OnLine
    resetChartDataOnline() {
      this.chartData = {
        salesByStatus: [],
        salesByType: [],
        salesByBranch: [],
        salesByPlatform: [],
        dailyTrend: [],
        notificationStatus: []
      };
    },
    
    // Métodos asíncronos para cada gráfico de OnLineSales
    async updateStatusChartAsyncOnline(dataSource) {
      try {
        const { AsyncDataProcessor } = await import('@/utils/asyncDataProcessor');
        
        const aggregation = await AsyncDataProcessor.groupAndCount(
          dataSource,
          (sale) => sale.estado,
          (sale) => this.parseMonetaryValue(sale.itemVendido?.valorPlan) || 0
        );

        this.chartData.salesByStatus = Object.entries(aggregation)
          .map(([status, data]) => ({
            label: status,
            value: data.count,
            amount: data.amount
          }))
          .sort((a, b) => b.value - a.value);
      } catch (error) {
        this.$logger.error('Error procesando gráfico de estado:', error);
      }
    },

    async updateTypeChartAsync(dataSource) {
      try {
        const { AsyncDataProcessor } = await import('@/utils/asyncDataProcessor');
        
        const aggregation = await AsyncDataProcessor.groupAndCount(
          dataSource,
          (sale) => sale.tipoTransaccion,
          (sale) => this.parseMonetaryValue(sale.itemVendido?.valorPlan) || 0
        );

        this.chartData.salesByType = Object.entries(aggregation)
          .map(([type, data]) => ({
            label: type,
            value: data.count,
            amount: data.amount
          }))
          .sort((a, b) => b.value - a.value);
      } catch (error) {
        this.$logger.error('Error procesando gráfico de tipo:', error);
      }
    },

    async updateBranchChartAsyncOnline(dataSource) {
      try {
        const { AsyncDataProcessor } = await import('@/utils/asyncDataProcessor');
        
        const aggregation = await AsyncDataProcessor.groupAndCount(
          dataSource,
          (sale) => sale.sucursal?.nombreSucursal || 'Sin sucursal',
          (sale) => this.parseMonetaryValue(sale.itemVendido?.valorPlan) || 0
        );

        this.chartData.salesByBranch = Object.entries(aggregation)
          .map(([branch, data]) => ({
            label: branch,
            value: data.count,
            amount: data.amount
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 10); // Top 10 sucursales
      } catch (error) {
        this.$logger.error('Error procesando gráfico de sucursales:', error);
      }
    },

    async updatePlatformChartAsync(dataSource) {
      try {
        const { AsyncDataProcessor } = await import('@/utils/asyncDataProcessor');
        
        const aggregation = await AsyncDataProcessor.groupAndCount(
          dataSource,
          (sale) => sale.aplicacionOrigen || 'Sin plataforma',
          (sale) => this.parseMonetaryValue(sale.itemVendido?.valorPlan) || 0
        );

        this.chartData.salesByPlatform = Object.entries(aggregation)
          .map(([platform, data]) => ({
            label: platform,
            value: data.count,
            amount: data.amount
          }))
          .sort((a, b) => b.value - a.value);
      } catch (error) {
        this.$logger.error('Error procesando gráfico de plataforma:', error);
      }
    },

    async updateDailyTrendAsync(dataSource) {
      try {
        const { AsyncDataProcessor } = await import('@/utils/asyncDataProcessor');
        
        const aggregation = await AsyncDataProcessor.groupAndCount(
          dataSource,
          (sale) => {
            if (!sale.fecha) return null;
            try {
              const date = new Date(sale.fecha);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              }
            } catch (error) {
              return null;
            }
          },
          (sale) => this.parseMonetaryValue(sale.itemVendido?.valorPlan) || 0
        );

        this.chartData.dailyTrend = Object.entries(aggregation)
          .filter(([date]) => date !== 'null')
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, data]) => ({
            label: date,
            value: data.count,
            amount: data.amount
          }));
      } catch (error) {
        this.$logger.error('Error procesando tendencia diaria:', error);
      }
    },

    async updateNotificationChartAsync(dataSource) {
      try {
        const { AsyncDataProcessor } = await import('@/utils/asyncDataProcessor');
        
        const notificationData = {
          'Exitosas': { count: 0, amount: 0 },
          'Con Errores': { count: 0, amount: 0 },
          'Pendientes': { count: 0, amount: 0 }
        };

        await AsyncDataProcessor.processInChunks(
          dataSource,
          (chunk) => {
            chunk.forEach(sale => {
              const amount = this.parseMonetaryValue(sale.itemVendido?.valorPlan) || 0;
              const notifStatus = sale.notificacionClienteExitosa;
              
              if (notifStatus === true || notifStatus === 'true') {
                notificationData['Exitosas'].count++;
                notificationData['Exitosas'].amount += amount;
              } else if (notifStatus === false || notifStatus === 'false') {
                notificationData['Con Errores'].count++;
                notificationData['Con Errores'].amount += amount;
              } else {
                notificationData['Pendientes'].count++;
                notificationData['Pendientes'].amount += amount;
              }
            });
          },
          { chunkSize: 200 }
        );

        this.chartData.notificationStatus = Object.entries(notificationData)
          .map(([status, data]) => ({
            label: status,
            value: data.count,
            amount: data.amount
          }));
      } catch (error) {
        this.$logger.error('Error procesando gráfico de notificaciones:', error);
      }
    },
    
    renderCharts() {
      // Renderizar todos los gráficos
      this.$nextTick(() => {
        this.createStatusChart();
        this.createTypeChart();
        this.createBranchChart();
        this.createPlatformChart();
        this.createTrendChart();
        this.createNotificationChart();
      });
    },
    
    createStatusChart() {
      if (!this.$refs.statusChart || this.chartData.salesByStatus.length === 0) return;

      if (this.chartInstances.statusChart) {
        this.chartInstances.statusChart.destroy();
      }

      const getColorForStatus = (status) => {
        const colorMap = {
          'PAGADO': '#28a745',
          'PENDIENTE': '#ffc107',
          'EN CURSO': '#17a2b8',
          'ERROR': '#dc3545',
          'CANCELADO': '#6c757d',
          'RECHAZADO': '#dc3545',
          'EXPIRADO': '#343a40'
        };
        return colorMap[status] || '#17a2b8';
      };

      const ctx = this.$refs.statusChart.getContext('2d');
      this.chartInstances.statusChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: this.chartData.salesByStatus.map(item => item.label),
          datasets: [{
            data: this.chartData.salesByStatus.map(item => item.value),
            backgroundColor: this.chartData.salesByStatus.map(item => getColorForStatus(item.label)),
            borderWidth: 2,
            borderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { position: 'bottom' },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const total = data.datasets[0].data.reduce((sum, value) => sum + value, 0);
                const value = data.datasets[0].data[tooltipItem.index];
                const percentage = ((value / total) * 100).toFixed(1);
                const statusData = this.chartData.salesByStatus[tooltipItem.index];
                const amount = statusData ? statusData.amount : 0;
                return [
                  `${data.labels[tooltipItem.index]}:`,
                  `📊 Ventas: ${value} (${percentage}%)`,
                  `💰 Monto: ${this.formatCurrencyForCharts(amount)}`
                ];
              }
            }
          }
        }
      });
    },
    
    createTypeChart() {
      if (!this.$refs.typeChart || this.chartData.salesByType.length === 0) return;

      if (this.chartInstances.typeChart) {
        this.chartInstances.typeChart.destroy();
      }

      const ctx = this.$refs.typeChart.getContext('2d');
      this.chartInstances.typeChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.chartData.salesByType.map(item => item.label),
          datasets: [{
            label: 'Cantidad',
            data: this.chartData.salesByType.map(item => item.value),
            backgroundColor: '#007bff',
            borderColor: '#0056b3',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            y: { beginAtZero: true }
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem) => {
                const typeData = this.chartData.salesByType[tooltipItem.index];
                const amount = typeData ? typeData.amount : 0;
                return [
                  `📊 Ventas: ${tooltipItem.yLabel}`,
                  `💰 Monto: ${this.formatCurrencyForCharts(amount)}`
                ];
              }
            }
          },
          animation: {
            duration: 1000,
            easing: 'easeInOutQuart',
            onComplete: function() {
              const chartInstance = this.chart;
              const ctx = chartInstance.ctx;
              ctx.textBaseline = 'middle';
              ctx.font = 'bold 12px Arial';
              
              this.data.datasets.forEach((dataset, i) => {
                const meta = chartInstance.controller.getDatasetMeta(i);
                meta.data.forEach((bar, index) => {
                  const data = dataset.data[index];
                  const value = new Intl.NumberFormat('es-CL').format(data);
                  
                  // Calcular la altura de la barra
                  const barHeight = bar._model.y - bar._model.base;
                  
                  // Calcular el ancho del texto
                  const textWidth = ctx.measureText(value).width;
                  const textHeight = 12; // Aproximadamente la altura del texto
                  
                  // Decidir si mostrar dentro o fuera de la barra
                  if (Math.abs(barHeight) > textHeight + 10) {
                    // Mostrar dentro de la barra
                    ctx.textAlign = 'center';
                    ctx.fillStyle = '#fff';
                    const x = bar._model.x;
                    const y = bar._model.base - 10; // 10px desde la base
                    
                    // Agregar sombra para mejor legibilidad
                    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                    ctx.shadowBlur = 3;
                    ctx.shadowOffsetX = 1;
                    ctx.shadowOffsetY = 1;
                    
                    ctx.fillText(value, x, y);
                    
                    // Resetear sombra
                    ctx.shadowColor = 'transparent';
                    ctx.shadowBlur = 0;
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 0;
                  } else {
                    // Mostrar fuera de la barra (arriba)
                    ctx.textAlign = 'center';
                    ctx.fillStyle = '#444';
                    const x = bar._model.x;
                    const y = bar._model.y - 10;
                    ctx.fillText(value, x, y);
                  }
                });
              });
            }
          }
        }
      });
    },
    
    createBranchChart() {
      if (!this.$refs.branchChart || this.chartData.salesByBranch.length === 0) return;

      if (this.chartInstances.branchChart) {
        this.chartInstances.branchChart.destroy();
      }

      const ctx = this.$refs.branchChart.getContext('2d');
      this.chartInstances.branchChart = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels: this.chartData.salesByBranch.map(item => item.label),
          datasets: [{
            label: 'Ventas',
            data: this.chartData.salesByBranch.map(item => item.value),
            backgroundColor: '#17a2b8',
            borderColor: '#138496',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            x: { beginAtZero: true }
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem) => {
                const branchData = this.chartData.salesByBranch[tooltipItem.index];
                const amount = branchData ? branchData.amount : 0;
                return [
                  `📊 Ventas: ${tooltipItem.xLabel}`,
                  `💰 Monto: ${this.formatCurrencyForCharts(amount)}`
                ];
              }
            }
          },
          animation: {
            duration: 1000,
            easing: 'easeInOutQuart',
            onComplete: function() {
              const chartInstance = this.chart;
              const ctx = chartInstance.ctx;
              ctx.textBaseline = 'middle';
              ctx.font = 'bold 12px Arial';
              
              this.data.datasets.forEach((dataset, i) => {
                const meta = chartInstance.controller.getDatasetMeta(i);
                meta.data.forEach((bar, index) => {
                  const data = dataset.data[index];
                  const value = new Intl.NumberFormat('es-CL').format(data);
                  
                  // Calcular el ancho de la barra (horizontal)
                  const barWidth = bar._model.x - bar._model.base;
                  
                  // Calcular el ancho del texto
                  const textWidth = ctx.measureText(value).width;
                  
                  // Decidir si mostrar dentro o fuera de la barra
                  if (barWidth > textWidth + 20) {
                    // Mostrar dentro de la barra
                    ctx.textAlign = 'right';
                    ctx.fillStyle = '#fff';
                    const x = bar._model.x - 10;
                    const y = bar._model.y;
                    
                    // Agregar sombra para mejor legibilidad
                    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                    ctx.shadowBlur = 3;
                    ctx.shadowOffsetX = 1;
                    ctx.shadowOffsetY = 1;
                    
                    ctx.fillText(value, x, y);
                    
                    // Resetear sombra
                    ctx.shadowColor = 'transparent';
                    ctx.shadowBlur = 0;
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 0;
                  } else {
                    // Mostrar fuera de la barra (a la derecha)
                    ctx.textAlign = 'left';
                    ctx.fillStyle = '#444';
                    const x = bar._model.x + 5;
                    const y = bar._model.y;
                    ctx.fillText(value, x, y);
                  }
                });
              });
            }
          }
        }
      });
    },
    
    createPlatformChart() {
      if (!this.$refs.platformChart || this.chartData.salesByPlatform.length === 0) return;

      if (this.chartInstances.platformChart) {
        this.chartInstances.platformChart.destroy();
      }

      const getColorForPlatform = (platform) => {
        const colorMap = {
          'FLOW': '#20c997',
          'WEBPAY': '#fd7e14',
          'PAYPAL': '#0d6efd'
        };
        return colorMap[platform] || '#6c757d';
      };

      const ctx = this.$refs.platformChart.getContext('2d');
      this.chartInstances.platformChart = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: this.chartData.salesByPlatform.map(item => item.label),
          datasets: [{
            data: this.chartData.salesByPlatform.map(item => item.value),
            backgroundColor: this.chartData.salesByPlatform.map(item => getColorForPlatform(item.label)),
            borderWidth: 2,
            borderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { position: 'bottom' },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const total = data.datasets[0].data.reduce((sum, value) => sum + value, 0);
                const value = data.datasets[0].data[tooltipItem.index];
                const percentage = ((value / total) * 100).toFixed(1);
                const platformData = this.chartData.salesByPlatform[tooltipItem.index];
                const amount = platformData ? platformData.amount : 0;
                return [
                  `${data.labels[tooltipItem.index]}:`,
                  `📊 Ventas: ${value} (${percentage}%)`,
                  `💰 Monto: ${this.formatCurrencyForCharts(amount)}`
                ];
              }
            }
          }
        }
      });
    },
    
    createTrendChart() {
      if (!this.$refs.trendChart || this.chartData.dailyTrend.length === 0) return;

      if (this.chartInstances.trendChart) {
        this.chartInstances.trendChart.destroy();
      }

      const ctx = this.$refs.trendChart.getContext('2d');
      this.chartInstances.trendChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.chartData.dailyTrend.map(item => {
            const date = new Date(item.label);
            return date.toLocaleDateString('es-CL', { month: 'short', day: 'numeric' });
          }),
          datasets: [{
            label: 'Ventas Diarias',
            data: this.chartData.dailyTrend.map(item => item.value),
            borderColor: '#ffc107',
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.3
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            y: { beginAtZero: true }
          },
          tooltips: {
            callbacks: {
              title: (tooltipItems) => {
                const date = new Date(this.chartData.dailyTrend[tooltipItems[0].index].label);
                return date.toLocaleDateString('es-CL', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                });
              },
              label: (tooltipItem) => {
                const dailyData = this.chartData.dailyTrend[tooltipItem.index];
                const amount = dailyData ? dailyData.amount : 0;
                return [
                  `📊 Ventas: ${tooltipItem.yLabel}`,
                  `💰 Monto: ${this.formatCurrencyForCharts(amount)}`
                ];
              }
            }
          }
        }
      });
    },
    
    createNotificationChart() {
      if (!this.$refs.notificationChart || this.chartData.notificationStatus.length === 0) return;

      if (this.chartInstances.notificationChart) {
        this.chartInstances.notificationChart.destroy();
      }

      const getColorForNotification = (status) => {
        const colorMap = {
          'Exitosas': '#28a745',
          'Con Errores': '#dc3545',
          'Pendientes': '#ffc107'
        };
        return colorMap[status] || '#6c757d';
      };

      const ctx = this.$refs.notificationChart.getContext('2d');
      this.chartInstances.notificationChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: this.chartData.notificationStatus.map(item => item.label),
          datasets: [{
            data: this.chartData.notificationStatus.map(item => item.value),
            backgroundColor: this.chartData.notificationStatus.map(item => getColorForNotification(item.label)),
            borderWidth: 2,
            borderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { position: 'bottom' },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const total = data.datasets[0].data.reduce((sum, value) => sum + value, 0);
                const value = data.datasets[0].data[tooltipItem.index];
                const percentage = ((value / total) * 100).toFixed(1);
                const notificationData = this.chartData.notificationStatus[tooltipItem.index];
                const amount = notificationData ? notificationData.amount : 0;
                return [
                  `${data.labels[tooltipItem.index]}:`,
                  `📊 Notificaciones: ${value} (${percentage}%)`,
                  `💰 Monto: ${this.formatCurrencyForCharts(amount)}`
                ];
              }
            }
          }
        }
      });
    },
    
    // ========================================
    // MÉTODOS AUXILIARES PARA FECHAS Y TÍTULO DINÁMICO
    // ========================================
    obtenerRangoFechasParaTitulo() {
      // Priorizar filtros de fecha si están definidos
      if (this.filters.dateFrom && this.filters.dateTo) {
        const fechaDesde = this.formatearFechaParaTitulo(this.filters.dateFrom);
        const fechaHasta = this.formatearFechaParaTitulo(this.filters.dateTo);
        
        return fechaDesde === fechaHasta 
          ? fechaDesde 
          : `${fechaDesde} al ${fechaHasta}`;
      }
      
      // Si no hay filtros de fecha, buscar en allFilteredRecords
      if (this.allFilteredRecords && this.allFilteredRecords.length > 0) {
        const fechas = this.allFilteredRecords
          .map(record => {
            const fecha = record.fechaCreacion;
            return fecha ? this.convertirFechaAPIaISO(fecha) : null;
          })
          .filter(fecha => fecha)
          .sort();
        
        if (fechas.length > 0) {
          const fechaMinima = fechas[0];
          const fechaMaxima = fechas[fechas.length - 1];
          
          const fechaDesde = this.formatearFechaParaTitulo(fechaMinima);
          const fechaHasta = this.formatearFechaParaTitulo(fechaMaxima);
          
          return fechaDesde === fechaHasta 
            ? fechaDesde 
            : `${fechaDesde} al ${fechaHasta}`;
        }
      }
      
      return '';
    },
    
    formatearFechaParaTitulo(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        const dia = fecha.getDate().toString().padStart(2, '0');
        const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
        const año = fecha.getFullYear();
        return `${dia}-${mes}-${año}`;
      } catch (error) {
        this.$logger.warn('Error al formatear fecha para título:', error);
        return '';
      }
    },
    
    convertirFechaAPIaISO(fechaAPI) {
      if (!fechaAPI) return null;
      try {
        // Si ya es ISO, devolverla tal como está
        if (fechaAPI.includes('T') || fechaAPI.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return fechaAPI;
        }
        
        // Si es DD-MM-YYYY, convertir a ISO
        if (fechaAPI.match(/^\d{2}-\d{2}-\d{4}$/)) {
          const [dia, mes, año] = fechaAPI.split('-');
          return `${año}-${mes}-${dia}`;
        }
        
        // Intentar parsear como fecha estándar
        const fecha = new Date(fechaAPI);
        return fecha.toISOString().split('T')[0];
      } catch (error) {
        this.$logger.warn('Error al convertir fecha API a ISO:', error);
        return null;
      }
    },
    
    // ========================================
    // MÉTODO DE EXPORTACIÓN DE KPIs A PDF
    // ========================================
    async exportKPIsToPDF() {
      try {
        this.$swal.fire({
          title: "Generando PDF de KPIs...",
          text: "Preparando gráficos para exportación",
          showConfirmButton: false,
          allowOutsideClick: false,
          didOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Importar dinámicamente las librerías necesarias
        const html2canvas = (await import('html2canvas')).default;
        const jsPDF = (await import('jspdf')).jsPDF;

        // Asegurar que los gráficos estén visibles para captura
        const originalShowCharts = this.showChartsPanel;
        if (!this.showChartsPanel) {
          this.showChartsPanel = true;
          await this.$nextTick();
          await new Promise(resolve => setTimeout(resolve, 1000)); // Esperar a que se rendericen
        }

        // Capturar KPIs
        const kpisCanvas = await html2canvas(this.$refs.kpisSection, {
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false
        });

        // Capturar gráficos
        const chartsCanvas = await html2canvas(this.$refs.chartsSection, {
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false
        });

        // Crear PDF
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 15;
        const availableWidth = pageWidth - (2 * margin);
        let yPosition = margin;

        // ========================================
        // 1. AGREGAR CABECERA CON TÍTULO Y FECHA
        // ========================================
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('REPORTE KPIs - TRANSACCIONES ON-LINE', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 10;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const fechaGeneracion = new Date().toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        pdf.text(`Generado: ${fechaGeneracion}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 15;

        // ========================================
        // 2. AGREGAR IMAGEN DE KPIs
        // ========================================
        const kpisImgWidth = availableWidth;
        const kpisImgHeight = (kpisCanvas.height * kpisImgWidth) / kpisCanvas.width;
        
        // Verificar si cabe en la página actual
        if (yPosition + kpisImgHeight > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
        
        pdf.addImage(
          kpisCanvas.toDataURL('image/png'),
          'PNG',
          margin,
          yPosition,
          kpisImgWidth,
          kpisImgHeight
        );
        yPosition += kpisImgHeight + 10;

        // ========================================
        // 3. AGREGAR IMAGEN DE GRÁFICOS
        // ========================================
        const chartsImgWidth = availableWidth;
        const chartsImgHeight = (chartsCanvas.height * chartsImgWidth) / chartsCanvas.width;

        // Verificar si cabe en la página actual
        if (yPosition + chartsImgHeight > pageHeight - 20) {
          pdf.addPage();
          yPosition = margin;
          
          // Agregar título de sección en nueva página
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('ANÁLISIS VISUAL DE TRANSACCIONES', margin, yPosition);
          yPosition += 10;
        }

        pdf.addImage(
          chartsCanvas.toDataURL('image/png'),
          'PNG',
          margin,
          yPosition,
          chartsImgWidth,
          chartsImgHeight
        );

        // Agregar footer en cada página
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          
          // Footer centrado
          pdf.setFontSize(8);
          pdf.setTextColor(100);
          const footerText = "Informe generado desde CentralGym App - https://centralgym.cl";
          const textWidth = pdf.getStringUnitWidth(footerText) * pdf.internal.getFontSize() / pdf.internal.scaleFactor;
          const textX = (pageWidth - textWidth) / 2;
          pdf.text(footerText, textX, pageHeight - 15);
          
          // Total registros (izquierda)
          const totalText = `Total registros analizados: ${this.totalFilteredRecords}`;
          pdf.text(totalText, margin, pageHeight - 15);
          
          // Número de página (derecha)
          const pageText = `Página ${i} de ${totalPages}`;
          const pageTextWidth = pdf.getStringUnitWidth(pageText) * pdf.internal.getFontSize() / pdf.internal.scaleFactor;
          pdf.text(pageText, pageWidth - margin - pageTextWidth, pageHeight - 15);
        }

        // ========================================
        // 4. GENERAR NOMBRE DE ARCHIVO Y DESCARGAR
        // ========================================
        const fechaArchivo = this.$moment ? this.$moment().format("DD-MM-yyyy - HH-mm-ss") : 
                           new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const fileName = `KPIs Transacciones OnLine - ${fechaArchivo}.pdf`;

        // Descargar PDF
        this.$swal.update({ text: 'Generando archivo PDF...' });
        pdf.save(fileName);

        // Restaurar estado original de gráficos
        this.showChartsPanel = originalShowCharts;

        this.$swal.close();
        
        this.$swal.fire({
          icon: 'success',
          title: '¡Exportación KPIs Exitosa!',
          html: `
            <div class="text-left">
              <p><strong>📊 Reporte generado exitosamente</strong></p>
              <p><strong>📁 Archivo:</strong> ${fileName}</p>
              <p><strong>📈 KPIs incluidos:</strong> Total Transacciones, Monto Total, Pagos Exitosos, Transacciones Pendientes</p>
              <p><strong>📊 Gráficos incluidos:</strong> ${this.showChartsPanel ? 'Análisis visual completo' : 'Solo KPIs (gráficos ocultos)'}</p>
              <p><strong>📋 Registros analizados:</strong> ${this.totalFilteredRecords}</p>
            </div>
          `,
          confirmButtonColor: '#6bd098',
          confirmButtonText: 'Perfecto'
        });

        this.$logger.log('✅ Exportación KPIs a PDF completada:', {
          archivo: fileName,
          registrosAnalizados: this.totalFilteredRecords,
          graficosIncluidos: this.showChartsPanel,
          kpisIncluidos: true
        });

      } catch (error) {
        this.$logger.error('Error al exportar KPIs a PDF:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error al generar PDF',
          text: 'No se pudo generar el archivo PDF. Inténtalo nuevamente.',
          confirmButtonColor: '#6bd098'
        });
      }
    },
    
    // ========================================
    // MÉTODOS DE FILTROS GUARDADOS
    // ========================================
    loadSavedFilters() {
      const saved = localStorage.getItem('onlineSalesFilters');
      if (saved) {
        try {
          this.savedFilters = JSON.parse(saved);
        } catch (error) {
          this.$logger.error('Error al cargar filtros guardados:', error);
          this.savedFilters = [];
        }
      }
    },
    saveSavedFilters() {
      localStorage.setItem('onlineSalesFilters', JSON.stringify(this.savedFilters));
    },
    
    openSaveFilterModal() {
      // Solo limpiar si no estamos editando
      if (!this.isEditingFilter) {
        this.filterToSave.name = '';
        this.filterToSave.description = '';
      }
      if (this.saveFilterModal) {
        this.saveFilterModal.show();
      }
    },
    
    closeSaveFilterModal() {
      if (this.saveFilterModal) {
        this.saveFilterModal.hide();
      }
      
      // Limpiar estado de edición al cerrar
      this.isEditingFilter = false;
      this.filterToEdit = null;
    },
    
    saveCurrentFilter() {
      if (!this.filterToSave.name.trim()) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Nombre requerido',
          text: 'Por favor, ingresa un nombre para el filtro',
          confirmButtonColor: '#6bd098'
        });
        return;
      }

      if (this.isEditingFilter && this.filterToEdit) {
        // MODO EDICIÓN: Actualizar filtro existente
        this.$logger.log('💾 Actualizando filtro existente:', this.filterToEdit.name);
        
        const filterIndex = this.savedFilters.findIndex(f => f.id === this.filterToEdit.id);
        if (filterIndex !== -1) {
          // Mantener ID original y fecha de creación, actualizar el resto
          this.savedFilters[filterIndex] = {
            ...this.savedFilters[filterIndex],
            name: this.filterToSave.name.trim(),
            description: this.filterToSave.description.trim(),
            filters: { ...this.filters },
            sorting: { ...this.sorting },
            updatedAt: new Date().toISOString() // Agregar fecha de actualización
          };
          
          this.saveSavedFilters();
          this.closeSaveFilterModal();
          
          this.$swal.fire({
            icon: 'success',
            title: '¡Filtro actualizado!',
            text: `El filtro "${this.filterToSave.name.trim()}" se ha actualizado correctamente`,
            confirmButtonColor: '#6bd098'
          });
        }
      } else {
        // MODO CREACIÓN: Crear nuevo filtro
        this.$logger.log('💾 Creando nuevo filtro:', this.filterToSave.name.trim());
        
        const filterConfig = {
          id: Date.now(),
          name: this.filterToSave.name.trim(),
          description: this.filterToSave.description.trim(),
          filters: { ...this.filters },
          sorting: { ...this.sorting },
          createdAt: new Date().toISOString()
        };

        this.savedFilters.push(filterConfig);
        this.saveSavedFilters();
        this.closeSaveFilterModal();

        this.$swal.fire({
          icon: 'success',
          title: '¡Filtro guardado!',
          text: `El filtro "${filterConfig.name}" se ha guardado correctamente`,
          confirmButtonColor: '#6bd098'
        });
      }
    },
    
    applySavedFilter(filter) {
      this.$logger.log('🔄 Aplicando filtro guardado localmente:', filter.name);
      
      // CAMBIO IMPORTANTE: Aplicar filtros sobre datos locales existentes
      // No realizar nueva consulta al servidor
      
      // 1. Aplicar configuración de filtros
      this.filters = { ...filter.filters };
      this.sorting = { ...filter.sorting };
      
      // 2. Sincronizar con filtros antiguos para UI
      this.syncFiltersFromNewToOld();
      
      // 3. Verificar si tenemos datos para filtrar localmente
      if (!this.allFilteredRecords || this.allFilteredRecords.length === 0) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Sin datos para filtrar',
          text: 'No hay datos cargados. Realiza una búsqueda primero para poder aplicar filtros guardados.',
          confirmButtonColor: '#6bd098'
        });
        return;
      }
      
      // 4. Aplicar filtros localmente sobre datos existentes
      this.applyFilterSetLocally(filter.filters, filter.sorting);
      
      this.$swal.fire({
        icon: 'success',
        title: '✅ Filtro aplicado localmente',
        text: `"${filter.name}" aplicado sobre ${this.allFilteredRecords.length} registros en memoria`,
        timer: 2500,
        showConfirmButton: false,
        timerProgressBar: true
      });
    },
    
    // ========================================
    // MÉTODO NUEVO: APLICAR CONJUNTO COMPLETO DE FILTROS LOCALMENTE
    // ========================================
    applyFilterSetLocally(filterSet, sortingConfig) {
      if (!this.originalFilteredRecords || this.originalFilteredRecords.length === 0) {
        this.$logger.warn('⚠️ No hay datos originales para filtrar');
        return;
      }
      
      this.$logger.log('🔧 Aplicando conjunto de filtros localmente:', {
        filtros: Object.keys(filterSet).filter(key => filterSet[key]),
        ordenamiento: sortingConfig,
        datosOriginales: this.originalFilteredRecords.length
      });
      
      // Comenzar con todos los datos originales (sin filtros previos)
      let filteredData = [...this.originalFilteredRecords];
      
      // Aplicar filtros básicos (simulando consulta al servidor)
      if (filterSet.status) {
        filteredData = filteredData.filter(record => record.estado === filterSet.status);
      }
      
      if (filterSet.trxType) {
        filteredData = filteredData.filter(record => record.tipoVenta === filterSet.trxType);
      }
      
      if (filterSet.branch) {
        filteredData = filteredData.filter(record => 
          record.nombreSucursal === this.getBranchName(filterSet.branch)
        );
      }
      
      if (filterSet.commerceOrder) {
        const orderId = parseInt(filterSet.commerceOrder);
        if (!isNaN(orderId)) {
          filteredData = filteredData.filter(record => record.commerceOrder === orderId);
        }
      }
      
      // Aplicar filtro de fechas si existe
      if (filterSet.dateFrom && filterSet.dateTo) {
        filteredData = this.filterRecordsByDateRange(filteredData, filterSet.dateFrom, filterSet.dateTo);
      }
      
      // Aplicar filtros avanzados
      const advancedFilters = {
        searchText: filterSet.searchText,
        platform: filterSet.platform,
        amountFrom: filterSet.amountFrom,
        amountTo: filterSet.amountTo,
        notificationStatus: filterSet.notificationStatus
      };
      
      const hasAdvancedFilters = Object.values(advancedFilters).some(value => 
        value && value.toString().trim() !== ''
      );
      
      if (hasAdvancedFilters) {
        filteredData = this.applyAdvancedFiltersToData(filteredData, advancedFilters);
      }
      
      // Aplicar ordenamiento si se especifica
      if (sortingConfig && sortingConfig.field) {
        filteredData = this.sortDataLocally(filteredData, sortingConfig.field, sortingConfig.direction);
      }
      
      this.$logger.log('📊 Filtros aplicados localmente:', {
        registrosOriginales: this.originalFilteredRecords.length,
        registrosFiltrados: filteredData.length,
        filtrosAplicados: Object.keys(filterSet).filter(key => filterSet[key]).length
      });
      
      // Actualizar datos y UI
      this.updateDataWithLocalFilters(filteredData);
    },
    
    // ========================================
    // MÉTODO NUEVO: ORDENAMIENTO LOCAL
    // ========================================
    sortDataLocally(data, field, direction = 'asc') {
      return data.sort((a, b) => {
        let valueA, valueB;
        
        // Obtener valores según el campo
        switch (field) {
          case 'commerceOrder':
            valueA = a.commerceOrder || 0;
            valueB = b.commerceOrder || 0;
            break;
          case 'itemVendido.valorPlan':
            valueA = this.parseMonetaryValue(a.itemVendido?.valorPlan) || 0;
            valueB = this.parseMonetaryValue(b.itemVendido?.valorPlan) || 0;
            break;
          case 'fechaCreacion':
            valueA = new Date(a.fechaCreacion);
            valueB = new Date(b.fechaCreacion);
            break;
          case 'tipoVenta':
            valueA = (a.tipoVenta || '').toLowerCase();
            valueB = (b.tipoVenta || '').toLowerCase();
            break;
          case 'responsable':
            valueA = (a.responsable || '').toLowerCase();
            valueB = (b.responsable || '').toLowerCase();
            break;
          case 'estado':
            valueA = (a.estado || '').toLowerCase();
            valueB = (b.estado || '').toLowerCase();
            break;
          case 'plataforma':
            valueA = (a.plataforma || '').toLowerCase();
            valueB = (b.plataforma || '').toLowerCase();
            break;
          case 'nombreSucursal':
            valueA = (a.nombreSucursal || '').toLowerCase();
            valueB = (b.nombreSucursal || '').toLowerCase();
            break;
          default:
            valueA = a[field] || '';
            valueB = b[field] || '';
        }
        
        // Comparar valores
        let result = 0;
        if (valueA < valueB) result = -1;
        else if (valueA > valueB) result = 1;
        
        // Aplicar dirección
        return direction === 'desc' ? -result : result;
      });
    },
    
    deleteSavedFilter(filter) {
      // Mostrar información detallada del filtro antes de eliminar
      const activeFiltersCount = this.getFilterSummary(filter);
      
      this.$swal.fire({
        title: '🗑️ ¿Eliminar Filtro Guardado?',
        html: `
          <div class="text-left">
            <p><strong>Filtro:</strong> ${filter.name}</p>
            <p><strong>Descripción:</strong> ${filter.description || 'Sin descripción'}</p>
            <p><strong>Configuración:</strong> ${activeFiltersCount}</p>
            <p><strong>Creado:</strong> ${this.formatDate(filter.createdAt)}</p>
            <hr>
            <p class="text-warning mb-0">⚠️ Esta acción no se puede deshacer</p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: '🗑️ Sí, eliminar',
        cancelButtonText: '✖️ Cancelar',
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          this.$logger.log('🗑️ Eliminando filtro:', filter.name);
          
          this.savedFilters = this.savedFilters.filter(f => f.id !== filter.id);
          this.saveSavedFilters();
          
          this.$swal.fire({
            icon: 'success',
            title: '¡Filtro eliminado!',
            text: `El filtro "${filter.name}" ha sido eliminado correctamente`,
            timer: 2500,
            showConfirmButton: false,
            timerProgressBar: true
          });
        }
      });
    },
    
    getBranchName(branchId) {
      const branch = this.branches.find(b => b._id === branchId);
      return branch ? branch.nombreSucursal : branchId;
    },
    
    // ========================================
    // MÉTODOS NUEVOS PARA FILTROS GUARDADOS MEJORADOS
    // ========================================
    
    getFilterSummary(filter) {
      // Contar cuántos filtros están activos
      const activeFilters = [];
      
      if (filter.filters.status) activeFilters.push('Estado');
      if (filter.filters.trxType) activeFilters.push('Tipo');
      if (filter.filters.platform) activeFilters.push('Plataforma');
      if (filter.filters.dateFrom && filter.filters.dateTo) activeFilters.push('Fechas');
      if (filter.filters.searchText) activeFilters.push('Texto');
      if (filter.filters.amountFrom || filter.filters.amountTo) activeFilters.push('Monto');
      if (filter.filters.notificationStatus) activeFilters.push('Notif.');
      if (filter.filters.branch) activeFilters.push('Sucursal');
      if (filter.filters.commerceOrder) activeFilters.push('ID');
      
      const count = activeFilters.length;
      return count > 0 ? `${count} filtro${count > 1 ? 's' : ''}` : 'Sin filtros';
    },
    
    formatAmountRange(amountFrom, amountTo) {
      if (amountFrom && amountTo) {
        return `${this.formatCurrency(amountFrom)} - ${this.formatCurrency(amountTo)}`;
      } else if (amountFrom) {
        return `Desde ${this.formatCurrency(amountFrom)}`;
      } else if (amountTo) {
        return `Hasta ${this.formatCurrency(amountTo)}`;
      }
      return '';
    },
    
    editSavedFilter(filter) {
      this.$logger.log('✏️ Editando filtro:', filter.name);
      
      // Marcar que estamos editando
      this.isEditingFilter = true;
      this.filterToEdit = filter;
      
      // Pre-cargar datos en el modal
      this.filterToSave.name = filter.name;
      this.filterToSave.description = filter.description || '';
      
      // Pre-cargar filtros actuales en el formulario
      this.filters = { ...filter.filters };
      this.sorting = { ...filter.sorting };
      
      // Sincronizar con filtros antiguos para que se vean en la UI
      this.syncFiltersFromNewToOld();
      
      // Abrir modal
      this.openSaveFilterModal();
    },
    
    duplicateSavedFilter(filter) {
      this.$logger.log('📋 Duplicando filtro:', filter.name);
      
      const duplicatedFilter = {
        id: Date.now(), // Nuevo ID único
        name: `${filter.name} (Copia)`,
        description: `Copia de: ${filter.description || filter.name}`,
        filters: { ...filter.filters },
        sorting: { ...filter.sorting },
        createdAt: new Date().toISOString()
      };
      
      this.savedFilters.push(duplicatedFilter);
      this.saveSavedFilters();
      
      this.$swal.fire({
        icon: 'success',
        title: '¡Filtro duplicado!',
        text: `Se ha creado una copia del filtro "${filter.name}"`,
        timer: 2000,
        showConfirmButton: false
      });
    },
    
    // ========================================
    // MÉTODOS DE EXPORTACIÓN
    // ========================================
    async exportToExcel() {
      try {
        this.isExporting = true;
        
        // Simulación para desarrollo
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        this.$swal.fire({
          icon: 'success',
          title: '¡Exportación exitosa!',
          text: 'Los datos de ventas online se han exportado correctamente (modo desarrollo)',
          confirmButtonColor: '#6bd098'
        });
        
        // TODO: Implementar exportación real
        
      } catch (error) {
        this.$logger.error('Error al exportar:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error al exportar',
          text: 'No se pudo generar el archivo de exportación. Por favor, intenta nuevamente.',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.isExporting = false;
      }
    },
    
    async exportToPDF() {
      try {
        this.isExporting = true;
        
        this.$swal.fire({
          title: "Generando Reporte PDF...",
          text: "Preparando datos para exportación",
          showConfirmButton: false,
          allowOutsideClick: false,
          didOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Importar dinámicamente las librerías necesarias
        const jsPDF = (await import('jspdf')).jsPDF;

        // Crear nuevo documento PDF
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4'
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 15;

        // ========================================
        // 1. PÁGINA DE TÍTULO
        // ========================================
        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        pdf.text('REPORTE DE VENTAS ONLINE', pageWidth / 2, 30, { align: 'center' });
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Sistema CentralGym', pageWidth / 2, 40, { align: 'center' });
        
        // Fecha del reporte
        pdf.setFontSize(12);
        const fechaGeneracion = new Date().toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        pdf.text(`Generado: ${fechaGeneracion}`, pageWidth / 2, 50, { align: 'center' });

        // Resumen de filtros aplicados
        let yPosition = 70;
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('FILTROS APLICADOS:', margin, yPosition);
        yPosition += 7;
        
        pdf.setFont('helvetica', 'normal');
        if (this.selectedStatus) {
          pdf.text(`• Estado: ${this.selectedStatus}`, margin + 5, yPosition);
          yPosition += 5;
        }
        if (this.selectedTrxType) {
          pdf.text(`• Tipo de Transacción: ${this.selectedTrxType}`, margin + 5, yPosition);
          yPosition += 5;
        }
        if (this.selectedBranch) {
          pdf.text(`• Sucursal: ${this.selectedBranch}`, margin + 5, yPosition);
          yPosition += 5;
        }
        if (this.dateFrom || this.dateTo) {
          const rangoFecha = `${this.dateFrom || 'Inicio'} - ${this.dateTo || 'Fin'}`;
          pdf.text(`• Rango de Fechas: ${rangoFecha}`, margin + 5, yPosition);
          yPosition += 5;
        }

        // ========================================
        // 2. RESUMEN DE KPIs
        // ========================================
        yPosition += 10;
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('RESUMEN EJECUTIVO:', margin, yPosition);
        yPosition += 10;

        // Cuadro de KPIs
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, yPosition - 5, pageWidth - (2 * margin), 40, 'F');
        
        const kpiStartX = margin + 10;
        const kpiSpacing = (pageWidth - (2 * margin) - 20) / 4;
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        
        // Total de ventas
        pdf.text('Total Ventas', kpiStartX, yPosition);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.text(String(this.totalOnlineSales), kpiStartX, yPosition + 8);
        
        // Monto total
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Monto Total', kpiStartX + kpiSpacing, yPosition);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.text(this.formatCurrency(this.totalAmount), kpiStartX + kpiSpacing, yPosition + 8);
        
        // Pagos exitosos
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Pagos Exitosos', kpiStartX + (kpiSpacing * 2), yPosition);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.text(String(this.paidSales), kpiStartX + (kpiSpacing * 2), yPosition + 8);
        
        // Pendientes
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Pendientes', kpiStartX + (kpiSpacing * 3), yPosition);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.text(String(this.pendingSales), kpiStartX + (kpiSpacing * 3), yPosition + 8);

        // ========================================
        // 3. TABLA DE VENTAS
        // ========================================
        pdf.addPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('DETALLE DE VENTAS ONLINE', margin, 20);

        // Preparar datos para la tabla
        const tableData = this.records.map(record => [
          record.idTransaccion,
          this.formatDate(record.updatedAt),
          record.socio?.nombre || 'Sin información',
          this.formatCurrency(record.monto),
          record.estado,
          record.tipoTransaccion,
          record.sucursal || 'N/A',
          record.plataforma || 'N/A'
        ]);

        // Configurar tabla manualmente
        const startY = 30;
        const rowHeight = 7;
        const colWidths = [15, 25, 60, 25, 25, 30, 30, 20];
        const headers = ['ID', 'Fecha', 'Cliente', 'Monto', 'Estado', 'Tipo', 'Sucursal', 'Plataforma'];
        
        // Dibujar encabezados
        pdf.setFillColor(108, 189, 152);
        pdf.rect(margin, startY, pageWidth - (2 * margin), rowHeight, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        
        let xPos = margin;
        headers.forEach((header, i) => {
          pdf.text(header, xPos + 2, startY + 5);
          xPos += colWidths[i];
        });
        
        // Dibujar filas de datos
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'normal');
        let yPos = startY + rowHeight;
        const maxRows = Math.floor((pageHeight - startY - 20) / rowHeight);
        let rowCount = 0;
        
        tableData.forEach((row, index) => {
          // Verificar si necesitamos nueva página
          if (rowCount >= maxRows) {
            pdf.addPage();
            yPos = 20;
            rowCount = 0;
            
            // Redibujar encabezados en nueva página
            pdf.setFillColor(108, 189, 152);
            pdf.rect(margin, yPos, pageWidth - (2 * margin), rowHeight, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFont('helvetica', 'bold');
            
            xPos = margin;
            headers.forEach((header, i) => {
              pdf.text(header, xPos + 2, yPos + 5);
              xPos += colWidths[i];
            });
            
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'normal');
            yPos += rowHeight;
          }
          
          // Alternar color de fondo
          if (index % 2 === 1) {
            pdf.setFillColor(245, 245, 245);
            pdf.rect(margin, yPos - rowHeight + 1, pageWidth - (2 * margin), rowHeight, 'F');
          }
          
          // Dibujar datos de la fila
          xPos = margin;
          row.forEach((cell, i) => {
            const cellText = String(cell || '');
            // Truncar texto si es muy largo
            const maxWidth = colWidths[i] - 4;
            const truncatedText = cellText.length > maxWidth/2 ? cellText.substring(0, maxWidth/2) + '...' : cellText;
            pdf.text(truncatedText, xPos + 2, yPos - 2);
            xPos += colWidths[i];
          });
          
          yPos += rowHeight;
          rowCount++;
        });

        // ========================================
        // 4. PIE DE PÁGINA EN TODAS LAS PÁGINAS
        // ========================================
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          
          // Footer
          pdf.setFontSize(8);
          pdf.setTextColor(100);
          pdf.text('CentralGym - Sistema de Gestión', margin, pageHeight - 10);
          pdf.text(`Página ${i} de ${totalPages}`, pageWidth - margin - 20, pageHeight - 10);
        }

        // ========================================
        // 5. GUARDAR PDF
        // ========================================
        const fechaArchivo = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const fileName = `Ventas_Online_${fechaArchivo}.pdf`;
        
        pdf.save(fileName);

        this.$swal.close();
        
        this.$swal.fire({
          icon: 'success',
          title: '¡Exportación PDF Exitosa!',
          html: `
            <div class="text-left">
              <p><strong>📄 Reporte generado correctamente</strong></p>
              <p><strong>📁 Archivo:</strong> ${fileName}</p>
              <p><strong>📊 Registros incluidos:</strong> ${this.records.length} de ${this.totalFilteredRecords} totales</p>
              <p><strong>💰 Monto total:</strong> ${this.formatCurrency(this.totalAmount)}</p>
            </div>
          `,
          confirmButtonColor: '#6bd098',
          confirmButtonText: 'Excelente'
        });
        
        this.$logger.log('✅ Exportación PDF completada:', {
          archivo: fileName,
          registrosIncluidos: this.records.length,
          totalRegistros: this.totalFilteredRecords
        });
        
      } catch (error) {
        this.$logger.error('Error al exportar PDF:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error al exportar PDF',
          text: 'No se pudo generar el reporte PDF. Por favor, intenta nuevamente.',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.isExporting = false;
      }
    },
    
    async exportSelectedToExcel() {
      try {
        this.isExporting = true;
        
        const selectedSales = this.records.filter(record => 
          this.selectedItems.includes(record._id)
        );

        // Simulación para desarrollo
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        this.$swal.fire({
          icon: 'success',
          title: '¡Exportación exitosa!',
          text: `${selectedSales.length} ventas online seleccionadas exportadas (modo desarrollo)`,
          confirmButtonColor: '#6bd098'
        });
        
        // TODO: Implementar exportación real de selección
        
      } catch (error) {
        this.$logger.error('Error al exportar selección:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error al exportar',
          text: 'No se pudo exportar la selección. Por favor, intenta nuevamente.',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.isExporting = false;
      }
    },
  },
  watch: {
    // Observar cambios en totalRecords para actualizar paginación
    totalRecords(newVal) {
      this.pagination.totalRecords = newVal;
      
      // Si la página actual es mayor que el total de páginas disponibles, resetear a la primera página
      const newTotalPages = Math.ceil(newVal / this.pagination.itemsPerPage);
      if (this.pagination.currentPage > newTotalPages && newTotalPages > 0) {
        this.pagination.currentPage = 1;
        this.currentPage = 0;
      }
    },
    // Observar cambios en records para actualizar selección (SOLO selección, no gráficos)
    records: {
      handler(newRecords) {
        // Limpiar selecciones que ya no existen
        if (newRecords && newRecords.length > 0) {
          const currentIds = newRecords.map(record => record._id);
          this.selectedItems = this.selectedItems.filter(id => currentIds.includes(id));
          this.updateAllSelectedState();
          // NOTA: Los gráficos se actualizan en watcher de allFilteredRecords
        } else {
          this.selectedItems = [];
          this.allSelected = false;
        }
      },
      immediate: true
    },
    // NUEVO: Observar cambios en allFilteredRecords para actualizar gráficos
    allFilteredRecords: {
      handler(newAllRecords) {
        // Los gráficos deben actualizarse cuando cambian TODOS los datos filtrados
        if (newAllRecords && newAllRecords.length > 0) {
          this.$logger.log('📊 Actualizando gráficos por cambio en allFilteredRecords:', newAllRecords.length, 'registros');
          this.updateChartData();
        }
      },
      immediate: true
    },
    // NUEVO: Observar cambios en localPagination para sincronizar paginación
    localPagination: {
      handler(newPagination) {
        if (newPagination) {
          // Sincronizar paginación local con componente
          this.pagination.currentPage = newPagination.currentPage;
          this.pagination.itemsPerPage = newPagination.itemsPerPage;
          this.$logger.log('📄 Paginación sincronizada:', newPagination);
        }
      },
      deep: true,
      immediate: true
    },
    // Observar cambios en selectedItems para actualizar allSelected
    selectedItems: {
      handler() {
        this.updateAllSelectedState();
      },
      deep: true
    },
    // Observar cambios en el panel de gráficos
    showChartsPanel: {
      handler(newVal) {
        if (newVal) {
          // Cuando se abre el panel, renderizar gráficos después de que el DOM se actualice
          this.$nextTick(() => {
            setTimeout(() => {
              this.renderCharts();
            }, 100);
          });
        }
      }
    },
    selectedStatus(newVal) {
      this.filters.status = newVal || '';
    },
    selectedTrxType(newVal) {
      this.filters.trxType = newVal || '';
    },
    selectedBranch(newVal) {
      this.filters.branch = newVal || '';
    },
    commerceOrderFilter(newVal) {
      this.filters.commerceOrder = newVal || '';
    },

    
    // Watchers para filtros avanzados (HABILITADOS - filtrado local con delay automático)
    // NOTA: Estos watchers aplican filtros localmente sobre los datos ya cargados
    'filters.searchText': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.$logger.log('📝 Texto de búsqueda cambió:', newVal);
          this.handleAdvancedFilterChange();
        }
      }
    },
    'filters.platform': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.$logger.log('🏢 Plataforma cambió:', newVal);
          this.handleAdvancedFilterChange();
        }
      }
    },
    'filters.amountFrom': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.$logger.log('💰 Monto desde cambió:', newVal);
          this.handleAdvancedFilterChange();
        }
      }
    },
    'filters.amountTo': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.$logger.log('💰 Monto hasta cambió:', newVal);
          this.handleAdvancedFilterChange();
        }
      }
    },
    'filters.notificationStatus': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.$logger.log('📧 Estado notificación cambió:', newVal);
          this.handleAdvancedFilterChange();
        }
      }
    }
  },
  mounted() {
    if (this.selectedStatus || this.selectedTrxType || this.selectedBranch || 
        this.filters.dateFrom || this.filters.dateTo || this.commerceOrderFilter) {
      this.syncFiltersFromOldToNew();
    }
    
    // Inicializar gráficos si es necesario
    this.$nextTick(() => {
      this.updateChartData();
    });
  },
  beforeDestroy() {
    // Limpiar instancias de gráficos
    Object.keys(this.chartInstances).forEach(key => {
      if (this.chartInstances[key]) {
        this.chartInstances[key].destroy();
        this.chartInstances[key] = null;
      }
    });
  },
};
</script>

<style>
/* Estilos básicos existentes */
.tbody {
  max-height: 600px;
  box-shadow: inset;
  box-shadow: inset -10px 0 10px -5px rgba(0, 0, 0, 0.5);
}
.tbody--shadow {
  box-shadow: inset 0px -10px 10px -10px rgba(0, 0, 0, 0.5);
}

/* Asegurar que todos los contenedores de filtros tengan el mismo ancho */
.filter-field {
  min-width: 0; /* Permite que flexbox reduzca el tamaño si es necesario */
}

/* Asegurar que todos los campos de formulario tengan el mismo ancho */
.filter-field .form-control,
.filter-field .form-select,
.filter-field .el-select {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box;
}

/* Prevenir que el contenido expanda los select */
.filter-field .el-select {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.btn-filter--border {
  border: 2px solid #f8f9fa !important;
  color: #fff !important;
  transition: all 0.2s ease;
}

.btn-filter--border:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background-color: rgba(255, 255, 255, 0.1) !important;
}
.cursor-pointer:hover {
  cursor: pointer;
}

/* ========================================
   ESTILOS PARA ETIQUETAS DE FILTROS BÁSICOS - VENTAS ONLINE v2.6
   ======================================== */

/* Etiquetas de filtros en desktop */
.card-header .form-label.text-light {
  color: #f8f9fa !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  margin-top: 0.5rem !important;      /* Separar del borde superior */
  margin-bottom: 0.375rem !important; /* Reducir distancia excesiva con campos */
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.025em;
}

.card-header .form-label.text-light i {
  opacity: 0.9;
  font-size: 0.7rem;
}

/* Etiquetas de filtros en móvil */
.text-white.font-weight-bold {
  color: #ffffff !important;
  font-weight: 700 !important;
  font-size: 0.875rem !important;
  margin-bottom: 0.5rem !important;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  letter-spacing: 0.025em;
}

.text-white.font-weight-bold i {
  opacity: 0.95;
  font-size: 0.8rem;
  margin-right: 0.25rem;
}

/* Ajuste de espaciado para filtros con etiquetas */
.card-header .flex-column {
  align-items: flex-start !important;
  min-height: 3.5rem;           /* Altura reducida para mejor proporción */
  justify-content: flex-start;  /* Alineación natural sin forzar separación */
}

/* Estilos específicos para la segunda fila de filtros */
.card-header .d-md-flex.justify-content-start {
  margin-top: 0.25rem;          /* Espaciado mínimo entre filas */
}

.card-header .d-md-flex.justify-content-start .flex-column {
  min-height: 3.25rem;          /* Altura ligeramente menor para segunda fila */
}

/* Campo ID optimizado para valores numéricos */
.card-header input[type="number"] {
  max-width: 100px !important;
  text-align: center;
}

/* Optimización para distribución de filtros con flex */
.card-header .d-md-flex .w-100 {
  width: 100% !important;
}

/* Espaciado entre campos de filtro */
.card-header .flex-column + .flex-column {
  margin-left: 1rem;
}

/* Responsive para filtros con etiquetas */
@media (max-width: 767.98px) {
  .text-white.font-weight-bold {
    font-size: 0.8rem !important;
    margin-bottom: 0.375rem !important;
  }
  
  .text-white.font-weight-bold i {
    font-size: 0.75rem;
  }
  
  /* Ocultar segunda fila en móvil (se usa panel colapsable) */
  .card-header .d-md-flex.justify-content-start {
    display: none !important;
  }
}

/* Estilos para la reorganización del header */
.card-header.bg-success {
  background-color: #28A745 !important;
  padding-bottom: 1rem;
}

/* Estilos para la segunda fila de botones */
.card-header .border-top {
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-color: rgba(255, 255, 255, 0.2) !important;
}

/* Espaciado mejorado para los botones de panel */
.btn-group .btn-filter--border {
  margin-left: 0.25rem;
  margin-right: 0.25rem;
  transition: all 0.2s ease;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

.btn-group .btn-filter--border:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-group .btn-filter--border:first-child {
  margin-left: 0;
}

.btn-group .btn-filter--border:last-child {
  margin-right: 0;
}

/* Estilos responsive para la segunda fila */
@media (max-width: 767.98px) {
  .card-header .btn-group {
    flex-direction: column;
    width: 100%;
  }
  
  .card-header .btn-group .btn {
    margin: 0.25rem 0;
    width: 100%;
  }
}

/* Estilos para la nueva estructura */
.table-loading-overlay {
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
}

.table.opacity-50 {
  opacity: 0.5;
  pointer-events: none;
}

/* Estilos para ordenamiento de columnas */
.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  position: relative;
}

.sortable-header:hover {
  background-color: rgba(0, 123, 255, 0.1);
  color: #007bff;
}

.sortable-header.sort-active {
  background-color: rgba(0, 123, 255, 0.15);
  color: #007bff;
  font-weight: 600;
}

.sortable-header i {
  font-size: 0.8rem;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.sortable-header:hover i {
  opacity: 1;
}

/* Estilos para la paginación */
.pagination .page-link {
  color: #6c757d;
  border-color: #dee2e6;
  transition: all 0.15s ease-in-out;
}

.pagination .page-link:hover {
  color: #495057;
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.pagination .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

.pagination .page-item.disabled .page-link {
  color: #6c757d;
  background-color: #fff;
  border-color: #dee2e6;
  cursor: not-allowed;
}

/* Estilos para la información de paginación */
.pagination-info {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.75rem;
  margin-bottom: 1rem;
}

/* Contenedor de paginación visible */
.pagination-container {
  background-color: #f8f9fa !important;
  border: 2px solid #007bff !important;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
  animation: fadeIn 0.3s ease-in-out;
}

.pagination-container h6 {
  color: #007bff !important;
  font-weight: 600;
  margin-bottom: 1rem !important;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.pagination-controls {
  border-top: 1px solid #dee2e6;
  padding-top: 0.75rem;
}

/* Selector de elementos por página */
.form-select-sm {
  padding: 0.25rem 1.5rem 0.25rem 0.5rem;
  font-size: 0.875rem;
  border-radius: 0.25rem;
  min-width: 70px;
}

/* Información de página responsive */
@media (max-width: 768px) {
  .pagination-info .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
  }
  
  .pagination-info .text-end {
    text-align: left !important;
    margin-top: 0.5rem;
  }
  
  .pagination-info .d-flex.align-items-center {
    margin-bottom: 0.5rem;
  }
}

/* Estilos para selección múltiple */
.form-check {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 1.5rem;
}

.form-check-input {
  margin: 0;
  cursor: pointer;
}

.form-check-input:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.table-primary {
  background-color: rgba(13, 110, 253, 0.1);
  border-color: rgba(13, 110, 253, 0.2);
}

.table-primary:hover {
  background-color: rgba(13, 110, 253, 0.15);
}

/* Estilos para acciones en lote */
.alert-primary {
  border-left: 4px solid #0d6efd;
  background-color: rgba(13, 110, 253, 0.1);
  border-color: rgba(13, 110, 253, 0.2);
}

/* Estilos para el resumen de totales */
.icon-shape {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.2rem;
}

.bg-light {
  background-color: #f8f9fa !important;
}

/* Estilos para filtros avanzados */
.card-header .btn-link {
  color: #495057;
  font-weight: 500;
}

.card-header .btn-link:hover {
  color: #007bff;
  text-decoration: none;
}

.btn-group-vertical .btn {
  margin-bottom: 0.25rem;
}

.btn-group-vertical .btn:last-child {
  margin-bottom: 0;
}

/* Estilos para filtros de fecha */
.form-control-sm {
  font-size: 0.875rem;
}

/* Animaciones para elementos colapsables */
.card-body {
  transition: all 0.3s ease;
}

/* Estilos para botones de exportación */
.btn-toolbar .btn-group {
  margin-right: 0.5rem;
}

/* Estilos para filtros guardados */
.border-primary {
  border-color: #0d6efd !important;
}

.bg-primary {
  background-color: #0d6efd !important;
}

.card.border-light {
  border-color: rgba(0, 0, 0, 0.125) !important;
  transition: all 0.2s ease;
}

.card.border-light:hover {
  border-color: #0d6efd !important;
  box-shadow: 0 0.125rem 0.25rem rgba(13, 110, 253, 0.25);
}

/* Estilos para panel de gráficos */
.border-warning {
  border-color: #ffc107 !important;
}

.bg-warning {
  background-color: #ffc107 !important;
}

.chart-container {
  position: relative;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 10px;
}

.chart-container canvas {
  max-width: 100%;
  height: 100% !important;
}

/* Estado cuando no hay datos */
.chart-container.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  background-color: #f8f9fa;
}

.chart-container.no-data::before {
  content: "📊 Sin datos para mostrar";
  font-size: 0.875rem;
  text-align: center;
}

/* Indicador de búsqueda */
.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

/* Spinner de carga en botones */
.btn .fa-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Estilos para modal de guardar filtro */
.modal-content {
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
  border-top-left-radius: calc(0.5rem - 1px);
  border-top-right-radius: calc(0.5rem - 1px);
}

.modal-footer {
  border-top: 1px solid #dee2e6;
  border-bottom-right-radius: calc(0.5rem - 1px);
  border-bottom-left-radius: calc(0.5rem - 1px);
}

/* Estilos para dropdown de filtros guardados */
.dropdown-menu {
  border-radius: 0.375rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.15);
}

.dropdown-item {
  padding: 0.5rem 1rem;
  transition: background-color 0.15s ease-in-out;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item.text-danger:hover {
  background-color: #f8d7da;
  color: #721c24;
}

/* Animaciones mejoradas */
.btn-group .btn,
.btn-toolbar .btn {
  transition: all 0.15s ease-in-out;
}

.card {
  transition: all 0.2s ease-in-out;
}

.alert {
  animation: slideInDown 0.3s ease-in-out;
}

@keyframes slideInDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Mejoras responsive */
@media (max-width: 768px) {
  /* Filtros principales */
  .row.align-items-end .col-md-2,
  .row.align-items-end .col-md-3 {
    margin-bottom: 0.5rem;
  }
  
  .row.align-items-end .col-md-2:last-child {
    margin-bottom: 0;
  }
  
  .row.align-items-end .col-md-2:last-child .d-flex {
    justify-content: start !important;
  }

  /* Paginación */
  .pagination {
    font-size: 0.875rem;
  }
  
  .pagination .page-link {
    padding: 0.375rem 0.75rem;
  }

  /* Botones verticales */
  .btn-group-vertical {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .btn-group-vertical .btn {
    margin-bottom: 0.5rem;
    width: 100%;
  }

  /* Iconos */
  .icon-shape {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }

  .card-body .row .col-lg-3 {
    margin-bottom: 1rem;
  }

  /* Toolbar */
  .btn-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-toolbar .btn-group {
    margin-bottom: 0.5rem;
    margin-right: 0;
  }

  /* Headers ordenables */
  .sortable-header {
    font-size: 0.875rem;
    padding: 0.5rem 0.25rem;
  }
  
  /* Acciones en lote responsive */
  .btn-group.w-100 {
    display: flex;
    flex-direction: column;
  }

  .btn-group.w-100 .btn {
    margin-bottom: 0.25rem;
    border-radius: 0.375rem !important;
  }

  .btn-group.w-100 .btn:last-child {
    margin-bottom: 0;
  }

  .alert-primary .row {
    flex-direction: column;
  }

  .alert-primary .col-md-6 {
    margin-bottom: 1rem;
  }

  .alert-primary .col-md-6:last-child {
    margin-bottom: 0;
  }

  .card.border-light .card-body {
    padding: 1rem;
  }

  /* Gráficos responsive */
  .chart-container {
    height: 150px !important;
    margin-bottom: 1rem;
    padding: 5px;
  }

  .chart-container.no-data {
    font-size: 0.75rem;
  }

  /* Tabla responsive */
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .table th,
  .table td {
    padding: 0.5rem 0.25rem;
  }

  /* Botones de acción en tabla */
  .btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }
}

/* Mejoras para tablets */
@media (min-width: 769px) and (max-width: 1024px) {
  .icon-shape {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }
  
  .btn-toolbar .btn-group .btn {
    font-size: 0.875rem;
    padding: 0.375rem 0.75rem;
  }
}

/* Mejoras adicionales para desktop */
@media (min-width: 768px) {
  .tbody {
    max-height: 500px;
    box-shadow: none;
  }
  .td--limiter {
    max-width: 100px;
  }
}

/* Estilos específicos para badges de estado */
.badge.bg-success {
  background-color: #198754 !important;
}

.badge.bg-warning {
  background-color: #ffc107 !important;
  color: #212529 !important;
}

.badge.bg-info {
  background-color: #0dcaf0 !important;
  color: #000 !important;
}

.badge.bg-danger {
  background-color: #dc3545 !important;
}

.badge.bg-secondary {
  background-color: #6c757d !important;
}

.badge.bg-dark {
  background-color: #212529 !important;
  color: #fff !important;
}

/* Estilos para filtros guardados mejorados */
.saved-filter-card {
  transition: all 0.3s ease;
  border: 2px solid transparent !important;
}

.saved-filter-card:hover {
  border-color: #0d6efd !important;
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.15);
  transform: translateY(-2px);
}

.saved-filter-card .card-header {
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa !important;
}

.saved-filter-card .card-title {
  color: #495057;
  font-weight: 600;
}

.saved-filter-card .filter-tags {
  min-height: 2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}

.saved-filter-card .filter-tags .badge {
  font-size: 0.65em;
  padding: 0.25em 0.5em;
  border-radius: 0.375rem;
}

.saved-filter-card .btn-group .btn {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}

.saved-filter-card .btn-primary {
  background-color: #0d6efd;
  border-color: #0d6efd;
  font-weight: 500;
  transition: all 0.2s ease;
}

.saved-filter-card .btn-primary:hover {
  background-color: #0b52cc;
  border-color: #0a4cb8;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(13, 110, 253, 0.3);
}

.saved-filter-card .btn-outline-info:hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.saved-filter-card .btn-outline-success:hover {
  background-color: #28a745;
  border-color: #28a745;
  color: white;
}

.saved-filter-card .btn-outline-danger:hover {
  background-color: #dc3545;
  border-color: #dc3545;
  color: white;
}

/* Animación para aparición de tarjetas */
.saved-filter-card {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive para filtros guardados */
@media (max-width: 768px) {
  .saved-filter-card .filter-tags .badge {
    font-size: 0.6em;
    margin-bottom: 0.25rem;
  }
  
  .saved-filter-card .btn-group {
    flex-direction: column;
  }
  
  .saved-filter-card .btn-group .btn {
    margin-bottom: 0.25rem;
    border-radius: 0.375rem !important;
  }
}

/* Estilos para estado vacío de filtros guardados */
.empty-state {
  padding: 2rem 1rem;
}

.empty-state .alert {
  border-radius: 0.5rem;
  border: 2px solid #bee5eb;
  background-color: #f0f8ff;
}

.empty-state .alert-heading {
  color: #0c5460;
  font-weight: 600;
}

.empty-state ol {
  text-align: left;
  padding-left: 1.5rem;
}

.empty-state ol li {
  margin-bottom: 0.5rem;
  color: #495057;
}

.empty-state .badge {
  font-size: 0.75em;
  padding: 0.3em 0.6em;
}

@media (max-width: 768px) {
  .empty-state {
    padding: 1rem 0.5rem;
  }
  
  .empty-state .alert {
    max-width: 100% !important;
  }
}

/* Estilos para notificaciones en tabla */
.text-success {
  color: #198754 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-secondary {
  color: #6c757d !important;
}

/* Mejoras para accesibilidad */
.btn:focus,
.form-control:focus,
.form-select:focus {
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
  border-color: #86b7fe;
}

/* Estilos para tooltips mejorados */
[title] {
  cursor: help;
}

/* Estilos finales de optimización */
.table-hover tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.075);
}

.text-monospace {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.fw-bold {
  font-weight: 700 !important;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Estilos para filtros preparados para el futuro */
.form-control:disabled,
.form-select:disabled {
  background-color: #f8f9fa !important;
  border-color: #dee2e6 !important;
  color: #6c757d !important;
  opacity: 0.7;
  cursor: not-allowed;
}

.form-control:disabled::placeholder {
  color: #6c757d !important;
  opacity: 0.7;
}

.badge.badge-secondary {
  background-color: #6c757d !important;
  color: #fff !important;
  font-size: 0.65em;
  padding: 0.2em 0.4em;
  border-radius: 0.25rem;
}

.badge.badge-success {
  background-color: #28a745 !important;
  color: #fff !important;
  font-size: 0.65em;
  padding: 0.2em 0.4em;
  border-radius: 0.25rem;
}

/* Alert para filtros avanzados */
.alert-sm {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffeaa7;
  color: #856404;
}

.alert-warning .fas {
  color: #f39c12;
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.alert-info .fas {
  color: #17a2b8;
}

/* Indicador de filtrado en progreso */
.alert-warning {
  background-color: #fff3cd;
  border-color: #ffeaa7;
  color: #856404;
  animation: pulse-warning 2s infinite;
}

@keyframes pulse-warning {
  0% { opacity: 1; }
  50% { opacity: 0.8; }
  100% { opacity: 1; }
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.1em;
}

/* Botones de panel - Estado normal (gris oscuro con borde blanco) */
.panel-toggle-btn.btn-filter--border {
  background-color: rgba(52, 58, 64, 0.8) !important;
  border-color: rgba(255, 255, 255, 0.8) !important;
  color: #fff !important;
  transition: all 0.3s ease !important;
}

/* Botones de panel - Estado activo (tono más claro) */
.panel-toggle-btn.btn-filter--border.active {
  background-color: rgba(108, 117, 125, 0.9) !important;
  border-color: rgba(255, 255, 255, 1) !important;
  color: #fff !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
}

/* Hover para ambos estados */
.panel-toggle-btn.btn-filter--border:hover {
  background-color: rgba(73, 80, 87, 0.9) !important;
  border-color: rgba(255, 255, 255, 1) !important;
  transform: translateY(-1px);
}



/* Estilos para filtros móviles */
.mobile-filters {
  background-color: #28a745;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.mobile-filters .form-label {
  color: #fff !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
}

.mobile-filters .form-control,
.mobile-filters .form-select {
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #495057;
}

.mobile-filters .form-control:focus,
.mobile-filters .form-select:focus {
  background-color: #fff;
  border-color: #fff;
  box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.25);
}

/* Estilos responsive adicionales para botones de panel */
@media (max-width: 768px) {
  .panel-toggle-btn.btn-filter--border {
    font-size: 0.75rem !important;
    padding: 0.375rem 0.5rem !important;
  }
  
  /* Tamaño consistente para todos los botones móviles */
  .mobile-filters .btn-group .btn,
  .mobile-btn-consistent {
    font-size: 0.75rem !important;
    padding: 0.375rem 0.5rem !important;
    height: 2.25rem !important;
    line-height: 1.2 !important;
    border-radius: 0.25rem !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  
  /* Ajustes específicos para botones de panel en móvil */
  .mobile-filters .panel-toggle-btn.btn-filter--border {
    background-color: #6c757d !important;
    border: 2px solid #fff !important;
    color: #fff !important;
  }
  
  .mobile-filters .panel-toggle-btn.btn-filter--border:hover {
    background-color: #5a6268 !important;
  }
  
  .mobile-filters .panel-toggle-btn.btn-filter--border.active {
    background-color: #5a6268 !important;
    border: 2px solid #fff !important;
    color: #fff !important;
  }
  
  /* Botones de acción en móvil */
  .mobile-filters .btn-info {
    background-color: #17a2b8 !important;
    border: 2px solid #17a2b8 !important;
    color: #fff !important;
  }
  
  .mobile-filters .btn-success {
    background-color: #28a745 !important;
    border: 2px solid #28a745 !important;
    color: #fff !important;
  }
  
  /* Corrección para campos de formulario móviles */
  .mobile-filters .row {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  
  .mobile-filters .col-6 {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
  
  .mobile-filters .col-6.pe-2 {
    padding-right: 0.25rem !important;
  }
  
  .mobile-filters .col-6.ps-2 {
    padding-left: 0.25rem !important;
  }
  
  /* Asegurar que los selects no se salgan del contenedor */
  .mobile-filters .form-select,
  .mobile-filters .el-select {
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  /* Espaciado consistente para iconos en botones móviles */
  .mobile-btn-consistent .fas,
  .mobile-btn-consistent .fa {
    margin-right: 0.25rem !important;
    font-size: 0.75rem !important;
  }
}

/* Indicadores de carga para gráficos - OnLineSales */
.chart-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 10;
  border-radius: 8px;
}

.chart-container {
  position: relative;
  min-height: 200px;
}

.chart-container.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
}

.chart-container.no-data::after {
  content: "Sin datos para mostrar";
  color: #6c757d;
  font-size: 14px;
}

/* Animación de progreso para exportación */
.export-progress-wrapper {
  padding: 20px;
}

.progress {
  height: 30px;
  font-size: 14px;
}

.progress-bar {
  transition: width 0.3s ease;
}
</style>
